import React, { useState, useEffect, useMemo, useRef } from 'react';
// @ts-ignore - Initialize explanation content (run once)
import { initializeExplanationContent } from './scripts/initializeExplanationContent';
import { seedTestMastermind } from './scripts/seedTestMastermind';
import { auth, db, googleProvider, linkedinProvider, storage } from './firebase';
import { signInWithRedirect, signInWithPopup, getRedirectResult, GoogleAuthProvider, signOut, onAuthStateChanged, User, updateProfile } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, getDocs, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { Seo } from './components/Seo';
import { SeoEn } from './components/SeoEn';
import AdminDashboard from './components/AdminDashboard';
import AdminMonitoringDashboard from './components/admin/AdminMonitoringDashboard';
import AdminPageNavigator from './components/admin/AdminPageNavigator';
import { usePageVisibility } from './hooks/usePageVisibility';
import { useGoalContext } from './hooks/useGoalContext';
import { GoalContextSwitcher } from './components/GoalContextSwitcher';
import CommunityDashboard from './components/CommunityDashboard';
import PublicExpertProfile from './pages/PublicExpertProfile';
import { AdminMuteProvider } from './components/admin/AdminMuteProvider';
import MembershipPage from './components/MembershipPage';
import TrainingCenter from './components/TrainingCenter';
// import MijnVerhaal from './components/MijnVerhaal'; // DISABLED: Using new ProfileSliderHomeFromVerhaal UI
import ProfileSliderHomeFromVerhaal from './components/Profilepage/ProfileSliderHomeFromVerhaal';
import ContentHub from './components/ContentHub';
import DragDropCardsEditor from './components/DragDropCardsEditor';
import ProfileHub from './components/ProfileHub';
import NetworkClubProfileHub from './components/networkClubs/NetworkClubProfileHub';
import MastermindCommunityHub from './components/networkClubs/MastermindCommunityHub';
import ClubVerhaalPage from './components/networkClubs/ClubVerhaalPage';
import GroupsManager from './components/networkClubs/GroupsManager';
// New main navigation components
import ConnectiesPage from './components/ConnectiesPage';
import ChatConnectionSystem from './components/ChatConnectionSystem';
import ChatInterface from './components/ChatInterface';
import ChatPage from './components/ChatPage';
import AgendaPage from './components/AgendaPage';
import InboxPage from './components/InboxPage';
import InboxBadge from './components/InboxBadge';
import { useUpcomingAgendaCount } from './hooks/useUpcomingAgendaCount';
import useInboxUnread from './hooks/useInboxUnread';
import SettingsPage from './components/SettingsPage';
import OnboardingWizard from './components/OnboardingWizard';
import LanguageSelector from './components/LanguageSelector';
import VibesPage from './components/VibesPage';
import AdminSchedulePage from './app/admin/schedule/page';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { FEATURE_SNIPPETS, SHOW_GEMINI_TEST, SHOW_SOCIAL_SHARING_DEMO } from './config/flags';
import OobActionHandler from './components/OobActionHandler';
import { ConnectionFlowContainer } from './components/ConnectionFlowContainer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { cleanBlobUrls } from './utils/profileUtils';
import GlobalCallPopup from './components/GlobalCallPopup';
import GlobalAgendaNotification from './components/GlobalAgendaNotification';
import LLMAnalysesDropdown from './components/LLMAnalysesDropdown';
import FloatingMeetingNotification from './components/FloatingMeetingNotification';
import { useMissedConnectionRequests } from './hooks/useMissedConnectionRequests';
import ConnectionAcceptedModal from './components/ConnectionAcceptedModal';
import AIAssistantOverlay from './components/AIAssistant/AIAssistantOverlay';
import GeminiTestComponent from './components/GeminiTestComponent';
import PlanningNotifTest from './components/PlanningNotifTest';
import FysiekePhaseTest from './components/FysiekePhaseTest';
import { useSubscription } from './hooks/useSubscription';
import { apiHealthMonitor } from './services/apiHealthMonitor';
import { analyticsService } from './services/analyticsService';
import SocialSharingDemo from './components/SocialSharingDemo';
import SocialMediaHub from './components/SocialMediaHub';
import BetaBadge from './components/BetaBadge';
import LinkedInCallback from './components/oauth/LinkedInCallback';
import OutlookCallback from './components/oauth/OutlookCallback';
import TwitterCallback from './components/oauth/TwitterCallback';
import PinterestCallback from './components/oauth/PinterestCallback';
import YouTubePodcastFeed from './components/YouTubePodcastFeed';
import FeedbackWidget from './components/FeedbackWidget';
import CreditsDisplay from './components/CreditsDisplay';
import { useCredits } from './hooks/useCredits';
import PlatformExplanationMode from './components/PlatformExplanationMode';
import { trackLogin } from './services/expertStatusService';
import PageInviteMenuItem from './components/PageInviteMenuItem';
import GroupJoinLandingModal from './components/GroupJoinLandingModal';
import EventLightbox from './components/networkClubs/components/EventLightbox';
import ManagerAccessRequestHandler from './components/ManagerAccessRequestHandler';
import AccountTransferRequestPopup from './components/AccountTransferRequestPopup';
import { useUserEntities } from './hooks/useUserEntities';
import { CommunityIcon, ThinkIcon, PartnerIcon, InsightIcon, BusinessIcon, ProfileIcon, SettingsIcon, FiltersIcon, PremiumIcon, NextIcon, HomeIcon, ICON_STYLES } from './components/icons';
import { ExperienceModeProvider } from './components/ExperienceMode/ExperienceModeProvider';
import { ExperienceModeOverlay } from './components/ExperienceMode/ExperienceModeOverlay';
import { GreenTourButton } from './components/ExperienceMode/GreenTourButton';
import { PageCompletionCard } from './components/ExperienceMode/PageCompletionCard';
import BottomNavigation from './components/mobile/BottomNavigation';
import MobileTopBar from './components/mobile/MobileTopBar';
import PageTabs from './components/mobile/PageTabs';
import LoginPage from './components/auth/LoginPage';
// SocialMediaStatus removed - not needed in navigation bar
import { LayoutDashboard, Users, Calendar, BarChart3, User as UserIcon, HandshakeIcon, Target, MessageCircle, Link as LinkIcon, Award, Share2, ChevronDown, CreditCard } from './components/icons';

import './i18n'; // Initialize i18n
import './index.css';
import './styles/superchat.css';
import './styles/experienceMode.css';
import 'leaflet/dist/leaflet.css'; // Leaflet map styles

import { UserProfile } from './types';
import { hasCompletedRequiredOnboardingFields } from './lib/profileCompletion';
import { applyDarkMode } from './utils/themeUtils';
import { applyBlueLightFilter } from './utils/blueLight';
import { isPhoneViewport } from './utils/mobileViewport';
// Motion system imports
import { AutoFadeNavbar } from './components/ui/auto-fade-navbar';

// Lazy-loaded pages
const SubscriptionPage = React.lazy(() => import('./components/SubscriptionPage'));

// Feature flag for new profile UI - REMOVED: Always use new UI
// const ENABLE_NEW_STORY_UI = true; // No longer needed - new UI is always active

// Helper functies voor paginadetectie
const getCurrentPageName = (activePage: string): string => {
  const pageNames: Record<string, string> = {
    'dashboard': 'Dashboard',
    'agenda': 'Agenda',
    'connecties': 'Netwerk',
    'inbox': 'Inbox',
    'vibes': 'Vibes',
    'mijn-verhaal': 'Mijn Verhaal',
    'chat-system': 'Chat Systeem',
    'chat': 'Chat',
    'social-media': 'Social Media',
    'feeds': 'Feeds'
  };
  return pageNames[activePage] || 'deze pagina';
};

const getCurrentPageHash = (activePage: string): string => {
  return `#${activePage}`;
};

const App: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const { balance: creditsBalance } = useCredits(user?.uid || '');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState<'dashboard' | 'connecties' | 'chat-system' | 'chat' | 'agenda' | 'inbox' | 'mijn-verhaal' | 'club-verhaal' | 'vibes' | 'admin-schedule' | 'admin-monitoring' | 'gemini-test' | 'social-sharing' | 'social-media' | 'feeds' | 'expert-profile' | 'privacy' | 'terms' | 'planning-test' | 'fysieke-test' | 'abonnement'>('dashboard');
  const [expertSlug, setExpertSlug] = useState<string | null>(null); // For expert profile routing
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showDisplaySettings, setShowDisplaySettings] = useState(false);
  const [deniedEmail, setDeniedEmail] = useState<string | null>(null);
  // Global popup for newly accepted connections
  const [newConnectionPopup, setNewConnectionPopup] = useState<any | null>(null);
  const newConnectionNotifiedIds = useRef<Set<string>>(new Set());
  // ── Taken Reminder toast ─────────────────────────────────────────────────
  const [taskReminderToast, setTaskReminderToast] = useState<{ groupName: string; openCount: number; entityId: string } | null>(null);
  const reminderCheckedRef = useRef(false);
  // ─────────────────────────────────────────────────────────────────────────

  // Global toast for incoming connection requests
  const [connectionRequestToast, setConnectionRequestToast] = useState<{ id: string; fromName: string; fromPhoto?: string } | null>(null);
  // Toast for pending planning sessions (shown after login)
  const [pendingPlanningToast, setPendingPlanningToast] = useState<{ connectionId: string; otherUserName: string; inboxItemId: string } | null>(null);
  const notifiedRequestIds = useRef<Set<string>>(new Set());
  const initialRequestLoadDone = useRef(false);
  const [mastermindLiveInvite, setMastermindLiveInvite] = useState<{
    entityId: string;
    entityName: string;
    entityType: string;
    roomName: string;
    startedByUid: string;
    startedByName: string;
    startedAtMs: number;
  } | null>(null);
  const [liveToasts, setLiveToasts] = useState<Array<{
    entityId: string;
    entityName: string;
    entityType: string;
    roomName: string;
    startedByName: string;
    startedAtMs: number;
  }>>([]);
  const dismissedMastermindLiveKeysRef = useRef<Set<string>>(new Set());
  const lastMobileChromeScrollTopRef = useRef(0);
  const lastMobileChromeScrollTargetRef = useRef<EventTarget | null>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCreditsInfo, setShowCreditsInfo] = useState(false);
  const [mobileNavDimmed, setMobileNavDimmed] = useState(false);

  // ── Mobile sub-tabs ──────────────────────────────────────────────────────────
  const [mobileSubTabIdx, setMobileSubTabIdx] = useState(0);

  const MOBILE_SUB_TABS: Record<string, string[]> = {
    dashboard:  ["Feeds", "Video's", "Events", "Collaborations", "Club Nieuws"],
    connecties: ["Mijn Netwerk", "Suggesties", "Verzoeken", "ConnectieFlow", "Chats"],
    inbox:      ["Alle Berichten", "Ongelezen", "Notificaties"],
    agenda:     ["Kalender", "Doelstellingen", "Taken", "Team", "Agenda Sync"],
    vibes:      ["Overzicht", "Content", "Connecties", "Engagement", "Profiel"],
  };

  const MOBILE_PAGE_TITLES: Record<string, string> = {
    dashboard:  'SuperKonnected',
    connecties: 'Netwerk',
    inbox:      'Inbox',
    agenda:     'Agenda',
    vibes:      'Vibes',
    'mijn-verhaal': 'Mijn Verhaal',
    'social-media': 'Social Media',
  };
  const [profileSubPage, setProfileSubPage] = useState<'profile-hub' | 'networkclub-hub' | 'mastermind-hub' | 'membership' | 'training' | 'content' | 'cards-editor' | 'english' | 'llm-dashboard' | 'groepsbeheer' | null>(null);
  const [selectedUserForStory, setSelectedUserForStory] = useState<string | null>(null);
  const [storyReturnPage, setStoryReturnPage] = useState<{ activePage: string; profileSubPage: string | null } | null>(null);
  const [selectedClubForStory, setSelectedClubForStory] = useState<{ entityId: string; entityType: 'networkclub' | 'mastermind' | 'community' } | null>(null);
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [showExplanationMode, setShowExplanationMode] = useState(false);
  const [showLLMDropdown, setShowLLMDropdown] = useState(false);
  
  // Get user entities for navbar "Groups" tab (all roles, not just owner/organizer)
  const { entities: userEntities, showGroupsTab, isTestAccount } = useUserEntities(user?.uid, user?.email);

  // Geen auto-navigatie naar mastermind/netwerkclub bij laden: app opent altijd op homepage.
  // (Eerder: userEntities ging van 0→N na login en werd ten onrechte als "nieuwe groep" gezien.)

  // Sluit live-notificatie zodra gebruiker de meeting ingaat
  React.useEffect(() => {
    const handler = (e: Event) => {
      const { entityId } = (e as CustomEvent).detail as { entityId: string };
      setMastermindLiveInvite((prev) => (prev?.entityId === entityId ? null : prev));
    };
    window.addEventListener('mastermindJoined', handler);
    return () => window.removeEventListener('mastermindJoined', handler);
  }, []);

  // Global live meeting listener — all entity types (mastermind, networkclub, community).
  // Shows a non-blocking toast + optional full invite modal.
  React.useEffect(() => {
    if (!user?.uid) {
      setMastermindLiveInvite(null);
      setLiveToasts([]);
      dismissedMastermindLiveKeysRef.current.clear();
      return;
    }

    if (userEntities.length === 0) {
      setMastermindLiveInvite(null);
      setLiveToasts([]);
      return;
    }

    const unsubs: Array<() => void> = [];
    const liveByEntity = new Map<string, {
      entityId: string;
      entityName: string;
      entityType: string;
      roomName: string;
      startedByUid: string;
      startedByName: string;
      startedAtMs: number;
    }>();
    let cancelled = false;

    const publishLiveState = () => {
      if (cancelled) return;
      const list = Array.from(liveByEntity.values()).sort((a, b) => b.startedAtMs - a.startedAtMs);

      // Toast list: all live meetings (not dismissed)
      const visibleToasts = list.filter((inv) => {
        const key = `${inv.entityId}:${inv.roomName}`;
        return !dismissedMastermindLiveKeysRef.current.has(key);
      });
      setLiveToasts(visibleToasts.map(({ startedByUid: _, ...rest }) => rest));

      // Modal invite: show most recent non-dismissed one
      const top = visibleToasts[0] || null;
      if (!top) { setMastermindLiveInvite(null); return; }
      const inviteKey = `${top.entityId}:${top.roomName}`;
      if (dismissedMastermindLiveKeysRef.current.has(inviteKey)) {
        setMastermindLiveInvite(null);
        return;
      }
      setMastermindLiveInvite({ ...top, startedByUid: (liveByEntity.get(top.entityId) as any)?.startedByUid || '' });
    };

    userEntities.forEach((entity) => {
      const unsub = onSnapshot(
        doc(db, 'activeMeetings', entity.id),
        (snap) => {
          if (!snap.exists()) {
            liveByEntity.delete(entity.id);
            publishLiveState();
            return;
          }
          const data: any = snap.data();
          const startedAtMs =
            typeof data?.startedAt?.toMillis === 'function'
              ? data.startedAt.toMillis()
              : Date.now();
          const startedByUid: string = data.startedBy || '';
          if (startedByUid && startedByUid === user?.uid) {
            liveByEntity.delete(entity.id);
            publishLiveState();
            return;
          }
          liveByEntity.set(entity.id, {
            entityId: entity.id,
            entityName: entity.name || entity.type || 'Groep',
            entityType: entity.type,
            roomName: data.roomName || `${entity.id}-live`,
            startedByUid,
            startedByName: data.startedByName || 'Host',
            startedAtMs,
          });
          publishLiveState();
        },
        () => {
          liveByEntity.delete(entity.id);
          publishLiveState();
        }
      );
      unsubs.push(unsub);
    });

    return () => {
      cancelled = true;
      unsubs.forEach((unsub) => {
        try { unsub(); } catch {}
      });
    };
  }, [user?.uid, userEntities]);
  
  const entityDropdownRef = useRef<HTMLDivElement>(null);

  // Close entity dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (entityDropdownRef.current && !entityDropdownRef.current.contains(event.target as Node)) {
        setShowEntityDropdown(false);
      }
    };

    if (showEntityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEntityDropdown]);

  // Display settings state with persistence and body class effects (global for all pages)
  type DayNightMode = 'day' | 'night';
  type DisplaySettings = {
    focusMode: boolean;
    energyMode: boolean;
    blueLightFilter: boolean;
    dayNightMode: DayNightMode;
  };
  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>(() => {
    try {
      const saved = localStorage.getItem('displaySettings');
      if (saved) return JSON.parse(saved);
    } catch {}
    return { focusMode: false, energyMode: false, blueLightFilter: false, dayNightMode: 'day' };
  });

  // Ref die altijd de meest recente pagina-staat bijhoudt — voorkomt stale closure in event listeners
  const currentPageStateRef = React.useRef({ activePage, profileSubPage });
  React.useEffect(() => {
    currentPageStateRef.current = { activePage, profileSubPage };
  }, [activePage, profileSubPage]);

  // Function to navigate to a user's story page
  const navigateToUserStory = (userId: string) => {
    // Lees altijd de actuele staat via de ref, niet de closure-waarde
    const { activePage: curPage, profileSubPage: curSub } = currentPageStateRef.current;
    setStoryReturnPage({ activePage: curPage, profileSubPage: curSub });
    setSelectedUserForStory(userId);
    setActivePage('mijn-verhaal');
    setShowProfileMenu(false);
  };

  const handleStoryBack = React.useCallback(() => {
    if (storyReturnPage) {
      setActivePage(storyReturnPage.activePage as any);
      setProfileSubPage(storyReturnPage.profileSubPage as any);
      setStoryReturnPage(null);
    } else {
      setActivePage('dashboard');
    }
    setSelectedUserForStory(null);
  }, [storyReturnPage]);

  // Global listener: any component can open a user profile via custom event
  // Usage: window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: 'abc123' } }))
  React.useEffect(() => {
    const handleOpenUserProfile = (event: Event) => {
      const e = event as CustomEvent<{ userId: string }>;
      if (e.detail?.userId) navigateToUserStory(e.detail.userId);
    };
    window.addEventListener('openUserProfile', handleOpenUserProfile as EventListener);
    return () => window.removeEventListener('openUserProfile', handleOpenUserProfile as EventListener);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global listener: open club story page
  // Usage: window.dispatchEvent(new CustomEvent('openClubStory', { detail: { entityId: '...', entityType: 'networkclub' } }))
  React.useEffect(() => {
    const handleOpenClubStory = (event: Event) => {
      const e = event as CustomEvent<{ entityId: string; entityType: 'networkclub' | 'mastermind' | 'community' }>;
      if (e.detail?.entityId) {
        setStoryReturnPage({ activePage, profileSubPage });
        setSelectedClubForStory({ entityId: e.detail.entityId, entityType: e.detail.entityType || 'networkclub' });
        setActivePage('club-verhaal');
        setShowProfileMenu(false);
      }
    };
    window.addEventListener('openClubStory', handleOpenClubStory as EventListener);
    return () => window.removeEventListener('openClubStory', handleOpenClubStory as EventListener);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, profileSubPage]);
  const [viewMode, setViewMode] = useState<'admin' | 'member'>('member');
  
  // Page visibility (admin online/offline toggles)
  // CRITICAL: We destructure 'visibility' to trigger re-renders when toggles change
  const { isPageOnline, visibility } = usePageVisibility();
  const isAdminView = isAdmin && viewMode === 'admin';
  
  // Goal context for team collaboration permissions
  const { activeGoal, isInTeamContext } = useGoalContext(user?.uid);
  
  /** Returns true when the page should render; admins always see everything. */
  const canShowPage = (pageId: string): boolean => {
    if (isAdminView) return true; // admins bypass offline pages
    const result = isPageOnline(pageId);
    // Debug log
    if (typeof window !== 'undefined' && window.location.hash.includes(pageId)) {
    }
    return result;
  };
  
  /**
   * Check if user can access a page based on goal context permissions
   * @param pageId - The page identifier
   * @returns true if user has permission to access the page
   */
  const canAccessPage = (pageId: string): boolean => {
    // No active goal context → full access to personal environment
    if (!activeGoal) return true;
    
    // Admin always has access
    if (isAdminView) return true;
    
    // Check access level permissions
    if (activeGoal.accessLevel === 'all') {
      return true; // Full access to all pages
    }
    
    if (activeGoal.accessLevel === 'agenda-only') {
      // Only allow agenda page
      return pageId === 'agenda';
    }
    
    return false;
  };

  const redirectingRef = useRef(false); // Prevent multiple redirects in quick succession
  const navigatingRef = useRef(false); // Track when user is actively navigating
  const hasRedirectedRef = useRef(false); // Track if we've already redirected to onboarding
  const guardInitializedRef = useRef(false); // Track if guard has done initial check
  const redirectResultCheckedRef = useRef(false); // Prevent null auth state from winning during Google redirect
  const [showOobHandler, setShowOobHandler] = useState(false);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const [pendingGroupInvite, setPendingGroupInvite] = useState<{
    entityId: string; entityType: string; clubName: string; invitedBy: string;
  } | null>(null);
  const [showGroupJoinModal, setShowGroupJoinModal] = useState(false);
  const [pendingEventInvite, setPendingEventInvite] = useState<any | null>(null);
  const [pendingEventQueue, setPendingEventQueue] = useState<any[]>([]);
  const [isOnboardingRoute, setIsOnboardingRoute] = useState(false);
  const [currentHashState, setCurrentHashState] = useState(typeof window !== 'undefined' ? window.location.hash : '');
  const [showConnectionFlow, setShowConnectionFlow] = useState(false);
  const [showOAuthCallback, setShowOAuthCallback] = useState(false);

  // Missed connection requests hook
  const { hasMissedRequests, clearMissedRequests} = useMissedConnectionRequests();
  const { hasUnread: hasUnreadInbox, unreadCount: inboxUnreadCount } = useInboxUnread(userProfile?.uid);
  const { count: upcomingAgendaCount, markAsSeen: markAgendaAsSeen } = useUpcomingAgendaCount(userProfile?.uid);
  
  // Subscription hook
  const { 
    subscription, 
    loading: subscriptionLoading, 
    canAccessFeature, 
    startTrial, 
    upgradeToPremium,
    isTrialActive,
    getRemainingTrialDays
  } = useSubscription(user);
  const currentTier = subscription?.tier ?? 'free';

  // Cache userProfile in localStorage
  useEffect(() => {
    if (userProfile) {
      try {
        localStorage.setItem('cachedUserProfile', JSON.stringify(userProfile));
      } catch (e) {
        console.error('Error caching user profile:', e);
      }
    }
  }, [userProfile]);

  // Apply display settings globally across all pages
  useEffect(() => {
    try { 
      localStorage.setItem('displaySettings', JSON.stringify(displaySettings)); 
    } catch {}
    
    const b = document.body;
    b.classList.remove('focus-mode', 'energy-mode', 'energy-mode-active', 'blue-light-filter', 'day-mode', 'night-mode');
    
    if (displaySettings.focusMode) b.classList.add('focus-mode');
    if (displaySettings.energyMode) b.classList.add('energy-mode-active');
    if (displaySettings.blueLightFilter) b.classList.add('blue-light-filter');
    b.classList.add(displaySettings.dayNightMode === 'day' ? 'day-mode' : 'night-mode');
    
    // Apply utilities
    applyDarkMode(displaySettings.dayNightMode === 'night');
    applyBlueLightFilter(displaySettings.blueLightFilter);
  }, [displaySettings]);

  useEffect(() => {
    const updatePhoneViewportClass = () => {
      document.body.classList.toggle('phone-viewport', isPhoneViewport());
      document.body.classList.toggle(
        'phone-landscape',
        isPhoneViewport() && window.matchMedia('(orientation: landscape)').matches
      );
      applyDarkMode(displaySettings.dayNightMode === 'night');
    };

    updatePhoneViewportClass();
    window.addEventListener('resize', updatePhoneViewportClass);
    window.addEventListener('orientationchange', updatePhoneViewportClass);
    return () => {
      window.removeEventListener('resize', updatePhoneViewportClass);
      window.removeEventListener('orientationchange', updatePhoneViewportClass);
    };
  }, [displaySettings.dayNightMode]);

  // Close profile menu when navigating to a subpage
  useEffect(() => {
    if (profileSubPage) {
      setShowProfileMenu(false);
    }
  }, [profileSubPage]);

  // Markeer agenda als gezien zodra de gebruiker de agenda-pagina opent.
  // Dit reset de badge-teller: alleen events aangemaakt NA dit moment tellen mee.
  useEffect(() => {
    if (activePage === 'agenda') {
      markAgendaAsSeen();
    }
  }, [activePage, markAgendaAsSeen]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showProfileMenu) {
        const target = event.target as Element;
        // Check of de klik binnen het menu is (inclusief buttons)
        const menuContainer = target.closest('[data-profile-menu]');
        if (!menuContainer) {
          setShowProfileMenu(false);
        }
      }
    };

    // Gebruik click event in plaats van mousedown voor betere compatibiliteit
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [showProfileMenu]);

  useEffect(() => {
    const handleMobileFeedChrome = (event: Event) => {
      const hidden = Boolean((event as CustomEvent<{ hidden?: boolean }>).detail?.hidden);
      setMobileNavDimmed(hidden);
    };

    window.addEventListener('mobileFeedChromeVisibility', handleMobileFeedChrome as EventListener);
    return () => window.removeEventListener('mobileFeedChromeVisibility', handleMobileFeedChrome as EventListener);
  }, []);

  useEffect(() => {
    setMobileNavDimmed(false);
    lastMobileChromeScrollTopRef.current = 0;
    lastMobileChromeScrollTargetRef.current = null;
  }, [activePage, profileSubPage, mobileSubTabIdx]);

  useEffect(() => {
    const handleMobileScroll = (event: Event) => {
      if (!isPhoneViewport()) return;
      if (showProfileMenu || showCreditsInfo) return;

      const target = event.target;
      let nextTop = window.scrollY || document.documentElement.scrollTop || 0;
      if (target instanceof HTMLElement && target !== document.documentElement && target !== document.body) {
        nextTop = target.scrollTop;
      }

      if (lastMobileChromeScrollTargetRef.current !== target) {
        lastMobileChromeScrollTargetRef.current = target;
        lastMobileChromeScrollTopRef.current = nextTop;
        if (nextTop <= 12) setMobileNavDimmed(false);
        return;
      }

      const delta = nextTop - lastMobileChromeScrollTopRef.current;
      if (nextTop <= 12) {
        setMobileNavDimmed(false);
      } else if (delta > 2) {
        setMobileNavDimmed(true);
      } else if (delta < -2) {
        setMobileNavDimmed(false);
      }

      lastMobileChromeScrollTopRef.current = nextTop;
    };

    window.addEventListener('scroll', handleMobileScroll, true);
    return () => window.removeEventListener('scroll', handleMobileScroll, true);
  }, [showProfileMenu, showCreditsInfo]);

  // Block body scroll when any modal is open
  useEffect(() => {
    if (showDisplaySettings || showCreditsInfo) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Block scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showDisplaySettings, showCreditsInfo]);


  // Load cached profile on start
  useEffect(() => {
    try {
      const cached = localStorage.getItem('cachedUserProfile');
      if (cached && !userProfile) {
        const parsed = JSON.parse(cached);
        setUserProfile(parsed);
      }
    } catch (e) {
      console.error('Error loading cached profile:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize API Health Monitor & Analytics
  useEffect(() => {
    if (user) {
      // Initialize analytics tracking
      analyticsService.init(user.uid);

      const isHealthAdmin = adminEmails.includes((user.email || '').toLowerCase());
      let timer: ReturnType<typeof setTimeout> | undefined;
      if (isHealthAdmin) {
        // Alleen admins krijgen browsermeldingen over ontbrekende AI/API keys.
        apiHealthMonitor.requestNotificationPermission();
        // Run initial health check na 5 seconden (om startup te laten voltooien)
        timer = setTimeout(() => {
          apiHealthMonitor.runHealthChecks();
        }, 5000);
      }

      return () => {
        if (timer) clearTimeout(timer);
        analyticsService.destroy();
      };
    }
  }, [user]);

  // Track lastSeenAt for inactivity reminders (updates on login + tab focus)
  useEffect(() => {
    if (!user?.uid) return;

    const writeLastSeen = () => {
      updateDoc(doc(db, 'userProfiles', user.uid), {
        lastSeenAt: serverTimestamp(),
      }).catch(() => {
        // Non-critical — silently ignore if profile doesn't exist yet
      });
    };

    // Write on mount (login / page load)
    writeLastSeen();

    // Write when user returns to the tab after being away
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') writeLastSeen();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user?.uid]);

  // Track page views on navigation
  useEffect(() => {
    if (user && activePage) {
      analyticsService.trackPageView(activePage);
    }
  }, [user, activePage]);

  // ── Taken Reminder check (client-triggered) ───────────────────────────────
  useEffect(() => {
    if (!user || reminderCheckedRef.current) return;
    // Alleen 1x per sessie controleren
    if (sessionStorage.getItem('taskReminderChecked')) return;
    reminderCheckedRef.current = true;
    sessionStorage.setItem('taskReminderChecked', '1');

    const checkReminders = async () => {
      try {
        // Haal actieve lidmaatschappen op
        const memSnap = await getDocs(query(
          collection(db, 'entityMembers'),
          where('userId', '==', user.uid),
          where('status', '==', 'active')
        ));
        if (memSnap.empty) return;

        for (const memDoc of memSnap.docs) {
          const mem = memDoc.data();
          const entityId = mem.entityId;
          if (!entityId) continue;

          // Haal entity settings op
          const settingsSnap = await getDocs(query(collection(db, 'entitySettings'), where('__name__', '==', entityId)));
          const settings = settingsSnap.docs[0]?.data();
          if (!settings?.taskReminderEnabled) continue;

          const reminderDays: number = settings.taskReminderDays ?? 7;
          const lastSent: any = settings.taskReminderLastSentAt;
          if (lastSent) {
            const lastMs = lastSent.toMillis ? lastSent.toMillis() : 0;
            const daysSinceLast = (Date.now() - lastMs) / (1000 * 60 * 60 * 24);
            if (daysSinceLast < reminderDays) continue;
          }

          // Tel open taken voor deze gebruiker
          const tasksSnap = await getDocs(query(
            collection(db, 'mastermindTasks'),
            where('entityId', '==', entityId),
            where('ownerUid', '==', user.uid),
            where('status', '==', 'open')
          ));
          if (tasksSnap.empty) continue;

          const openCount = tasksSnap.size;
          const groupName = mem.entityName ?? mem.clubName ?? 'je groep';

          // Schrijf inboxItem
          await addDoc(collection(db, 'inboxItems'), {
            userId: user.uid,
            type: 'task_reminder',
            category: 'reminder',
            priority: 'normal',
            title: `📋 ${openCount} open ${openCount === 1 ? 'taak' : 'taken'} in ${groupName}`,
            message: `Je hebt nog ${openCount} open commitment${openCount === 1 ? '' : 's'} staan. Bekijk ze in de Taken tab.`,
            isRead: false,
            createdAt: serverTimestamp(),
            actionData: { actionType: 'navigate_to_tasks', entityId, entityName: groupName },
          });

          // Update lastSentAt op entity (zodat niet iedere user het triggert)
          await updateDoc(doc(db, 'entitySettings', entityId), {
            taskReminderLastSentAt: serverTimestamp(),
          }).catch(() => {});

          // Toon toast (alleen voor de eerste groep)
          setTaskReminderToast({ groupName, openCount, entityId });
          break; // Één toast per sessie
        }
      } catch (err) {
        console.warn('[TaskReminder] Check mislukt:', err);
      }
    };

    // Wacht 8 seconden na app-load zodat andere initialisatie klaar is
    const timer = setTimeout(checkReminders, 8000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  // ─────────────────────────────────────────────────────────────────────────

  // Listen for onboarding completion to update userProfile state
  useEffect(() => {
    const handleOnboardingCompleted = (event: any) => {
      if (userProfile) {
        setUserProfile(prev => prev ? {
          ...prev,
          isOnboarded: true,
          onboarding: {
            status: 'done',
            completedAt: Date.now()
          }
        } : prev);
      }
    };

    window.addEventListener('onboardingCompleted', handleOnboardingCompleted);
    return () => window.removeEventListener('onboardingCompleted', handleOnboardingCompleted);
  }, [userProfile]);

  // Handle Google redirect result
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        console.log('🔄 Checking for Google redirect result...');
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('✅ Google login succeeded:', result.user.email);
          console.log('👤 User details:', {
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName
          });
          try {
            sessionStorage.removeItem('skGoogleLoginStarted');
          } catch {}
        } else {
          console.log('ℹ️ No redirect result found');
          try {
            if (sessionStorage.getItem('skGoogleLoginStarted') === '1') {
              sessionStorage.removeItem('skGoogleLoginStarted');
              setLoading(false);
            }
          } catch {}
        }
      } catch (error) {
        console.error('❌ Redirect result error:', error);
        try {
          sessionStorage.removeItem('skGoogleLoginStarted');
        } catch {}
        setLoading(false);
      } finally {
        redirectResultCheckedRef.current = true;
      }
    };

    handleRedirectResult();
  }, []);

  // URL Hash Routing
  useEffect(() => {
    const open = () => setShowDisplaySettings(true);
    window.addEventListener('openDisplaySettings', open as any);
    return () => window.removeEventListener('openDisplaySettings', open as any);
  }, []);

  // Global listener for newly accepted connections (where current user was sender)
  useEffect(() => {
    if (!user) {
      setNewConnectionPopup(null);
      newConnectionNotifiedIds.current = new Set();
      return;
    }

    let unsubscribe: (() => void) | undefined;

    import('firebase/firestore').then(({ collection, query, where, onSnapshot }) => {
      const { db } = require('./firebase');
      const connectionsRef = collection(db, 'connections');
      const q = query(
        connectionsRef,
        where('newForUserIds', 'array-contains', user.uid)
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change: any) => {
          if (change.type === 'added' || change.type === 'modified') {
            const id = change.doc.id;
            const data = change.doc.data();
            
            // Skip popup for known contact direct connects - CHECK FIRST before adding to notifiedIds
            if (data.knownContactDirectConnect) {
              console.log(`⏭️ Skipping known contact direct connect: ${id}`);
              // Still add to notifiedIds to prevent future checks
              newConnectionNotifiedIds.current.add(id);
              return;
            }
            
            if (!newConnectionNotifiedIds.current.has(id)) {
              newConnectionNotifiedIds.current.add(id);
              
              // ✅ ONLY show popup if connection was recently accepted (within 7 days)
              const createdAt = data.createdAt?.toDate?.() || new Date(0);
              const daysSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
              
              if (daysSinceCreation <= 7) {
                console.log(`📢 New connection notification: ${id} (${daysSinceCreation.toFixed(1)} days old)`);
                console.log('📋 Connection data:', { 
                  id, 
                  user1Id: data.user1Id, 
                  user2Id: data.user2Id,
                  user1Name: data.user1Name,
                  user2Name: data.user2Name,
                  currentUserId: user.uid,
                  knownContactDirectConnect: data.knownContactDirectConnect
                });
                setNewConnectionPopup({
                  id,
                  ...data
                });
              } else {
                console.log(`⏭️ Skipping old connection: ${id} (${daysSinceCreation.toFixed(1)} days old)`);
              }
            }
          }
        });
      }, (error) => {
        console.error('❌ Error listening for accepted connections:', error);
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  // Global listener for incoming connection requests → shows toast to receiver
  useEffect(() => {
    if (!user) {
      notifiedRequestIds.current = new Set();
      initialRequestLoadDone.current = false;
      return;
    }

    let unsubscribe: (() => void) | undefined;

    import('firebase/firestore').then(({ collection, query, where, onSnapshot }) => {
      const { db } = require('./firebase');
      const requestsRef = collection(db, 'connectionRequests');
      const q = query(
        requestsRef,
        where('toUserId', '==', user.uid),
        where('status', '==', 'pending')
      );

      unsubscribe = onSnapshot(q, (snapshot: any) => {
        // Skip the initial load to avoid showing toasts for existing requests
        if (!initialRequestLoadDone.current) {
          snapshot.docs.forEach((doc: any) => {
            notifiedRequestIds.current.add(doc.id);
          });
          initialRequestLoadDone.current = true;
          return;
        }

        snapshot.docChanges().forEach((change: any) => {
          if (change.type === 'added') {
            const id = change.doc.id;
            const data = change.doc.data();

            if (!notifiedRequestIds.current.has(id)) {
              notifiedRequestIds.current.add(id);
              
              console.log('🔔 New connection request received:', { id, from: data.fromUserName });
              setConnectionRequestToast({
                id,
                fromName: data.fromUserName || data.fromUserEmail || 'Iemand',
                fromPhoto: data.fromUserPhoto
              });

              // Auto-dismiss after 8 seconds
              setTimeout(() => {
                setConnectionRequestToast(prev => prev?.id === id ? null : prev);
              }, 8000);
            }
          }
        });
      }, (error: any) => {
        console.error('❌ Error listening for connection requests:', error);
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  // Helper function to mark connection notification as seen
  const markConnectionNotificationAsSeen = async (connectionId: string) => {
    if (!user) return;
    
    try {
      const { doc, updateDoc, arrayRemove } = await import('firebase/firestore');
      const { db } = require('./firebase');
      const connectionRef = doc(db, 'connections', connectionId);
      
      // Remove current user from newForUserIds array
      await updateDoc(connectionRef, {
        newForUserIds: arrayRemove(user.uid)
      });
      
      console.log(`✅ Marked connection ${connectionId} as seen for user ${user.uid}`);
    } catch (error) {
      console.error('❌ Error marking connection as seen:', error);
    }
  };

  const clearExpiredPlanningLock = async (connectionId: string) => {
    try {
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = require('./firebase');
      const ref = doc(db, 'connections', connectionId);
      await updateDoc(ref, {
        planningLock: {
          status: 'idle',
          lockedByUid: null,
          lockedByUsername: null,
          lockReason: 'cancelled',
          lockedAt: serverTimestamp(),
          expiresAt: null,
        },
      });
    } catch (err) {
      console.warn('Kon verlopen planningLock niet resetten', err);
    }
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      const pathPage = window.location.pathname.replace(/^\/+|\/+$/g, '');
      const route = hash || pathPage;
      const isAdminRoute = route === 'admin-schedule' || route === 'admin-monitoring';
      const canAccessAdminRoutes = isAdmin && viewMode === 'admin';

      // Hard block admin routes for non-admins (hash/manual nav)
      if (isAdminRoute && !canAccessAdminRoutes) {
        setActivePage('dashboard');
        setProfileSubPage(null);
        setShowProfileMenu(false);
        if (window.location.hash !== '#dashboard') {
          window.location.hash = '#dashboard';
        }
        return;
      }
      
      // Check for expert profile route first: /expert/{slug}
      const expertMatch = route.match(/^expert\/(.+)/);
      if (expertMatch) {
        setExpertSlug(expertMatch[1]);
        setActivePage('expert-profile');
        setProfileSubPage(null);
        setShowProfileMenu(false);
        return;
      }
      
      // Filter out test pages in funding mode
      const allowedPages = ['dashboard', 'connecties', 'agenda', 'inbox', 'mijn-verhaal', 'chat', 'social-media', 'vibes', 'feeds', 'privacy', 'terms', 'abonnement'];
      // Add test pages only if enabled
      if (SHOW_GEMINI_TEST) allowedPages.push('gemini-test');
      if (SHOW_SOCIAL_SHARING_DEMO) allowedPages.push('social-sharing');
      allowedPages.push('planning-test');
      allowedPages.push('fysieke-test');
      
      if (route && allowedPages.includes(route)) {
        setActivePage(route as any);
        setProfileSubPage(null);
        setShowProfileMenu(false);
      } else if (route && ['membership', 'training', 'content', 'cards-editor', 'profile-hub', 'networkclub-hub', 'mastermind-hub', 'groepsbeheer'].includes(route)) {
        setProfileSubPage(route as any);
        // Keep profile menu closed during route navigation; open only on explicit user click.
        setShowProfileMenu(false);
        setActivePage('dashboard'); // Keep main nav on dashboard when in profile submenu
      } else if (route === 'en') {
        setActivePage('dashboard');
        setProfileSubPage('english');
        setShowProfileMenu(false);
      } else if (route === 'en/vibescore') {
        setActivePage('vibes');
        setProfileSubPage('english');
        setShowProfileMenu(false);
      }
    };

    // Handle initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isAdmin, viewMode]);

  // Detect OAuth callback (LinkedIn, Facebook, etc.)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    
    // Also check for OAuth callback in path
    const path = window.location.pathname;
    if (path.includes('/oauth/linkedin/callback')) {
      console.log('🔐 LinkedIn OAuth callback detected in path');
      setShowOAuthCallback(true);
      return; // Exit early to prevent other logic from running
    }
    if (path.includes('/outlook-callback')) {
      console.log('🔐 Outlook OAuth callback detected in path');
      setShowOAuthCallback(true);
      return; // Exit early to prevent other logic from running
    }
    
    // Check if this is an OAuth callback (only if path matches)
    if ((code && state) || error) {
      console.log('🔐 OAuth callback detected');
      setShowOAuthCallback(true);
      return; // Exit early to prevent other logic from running
    }
  }, []);

  // Detect Firebase OOB actions (verifyEmail, resetPassword) and show inline handler
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');
    if (mode && oobCode) {
      setShowOobHandler(true);
    }
  }, []);

  // Read group invite link params from URL on mount
  // Supports both direct arrival (logged in) and post-login pickup via sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const entityId = params.get('inviteEntity');
    const entityType = params.get('inviteType') || 'mastermind';
    const clubName = decodeURIComponent(params.get('inviteClub') || '');
    const invitedBy = params.get('inviteBy') || '';

    if (entityId && clubName) {
      const invite = { entityId, entityType, clubName, invitedBy };
      setPendingGroupInvite(invite);
      try { sessionStorage.setItem('pendingGroupInvite', JSON.stringify(invite)); } catch {}
      // Clean invite params from URL without reloading
      try {
        const clean = window.location.origin + window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', clean);
      } catch {}
    } else {
      // Pick up invite that was stored before login redirect
      try {
        const stored = sessionStorage.getItem('pendingGroupInvite');
        if (stored) setPendingGroupInvite(JSON.parse(stored));
      } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show the group join modal once the user is authenticated and an invite is pending
  useEffect(() => {
    if (user && pendingGroupInvite && !showGroupJoinModal) {
      setShowGroupJoinModal(true);
    }
  }, [user, pendingGroupInvite, showGroupJoinModal]);

  // Watch for pending event invitations (lightbox shown when member comes online)
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { collection: col, query: q, where: wh, getDocs: gd } = await import('firebase/firestore');
        const snap = await gd(q(
          col(db, 'inboxItems'),
          wh('userId', '==', user.uid),
          wh('type', '==', 'event_invitation'),
        ));
        if (cancelled) return;
        const pending = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter((d: any) => d.rsvpResponse == null && !d.isArchived)
          .sort((a: any, b: any) => {
            const at = a.createdAt?.toMillis?.() || 0;
            const bt = b.createdAt?.toMillis?.() || 0;
            return bt - at;
          });
        if (pending.length > 0) {
          setPendingEventQueue(pending);
          setPendingEventInvite(pending[0]);
        }
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Update URL hash when activePage or profileSubPage changes
  // Use throttling to prevent "too many location API calls" errors
  useEffect(() => {
    // Throttle hash updates to prevent browser security errors
    const timeoutId = setTimeout(() => {
      // Prevent infinite loops by checking if hash already matches
      const currentHash = window.location.hash;
      let targetHash = '';
      
      if (profileSubPage) {
        targetHash = `#${profileSubPage}`;
      } else if (activePage !== 'dashboard') {
        targetHash = `#${activePage}`;
      } else {
        targetHash = '';
      }
      
      // Only update if hash is different to prevent security errors
      if (currentHash !== targetHash) {
        try {
          window.location.hash = targetHash;
        } catch (error) {
          // Silently catch security errors - hash might be locked by browser
          // Don't log to avoid console spam
        }
      }
    }, 100); // Throttle to max once per 100ms
    
    return () => clearTimeout(timeoutId);
  }, [activePage, profileSubPage]);

  // Support /onboarding path and optional force flags (?onboarding or ?forceOnboarding=1)
  useEffect(() => {
    const updateOnboardingRoute = () => {
      const path = window.location.pathname.replace(/\/+$/, '');
      const hash = window.location.hash;
      setCurrentHashState(hash); // Update hash state for immediate re-render
      const params = new URLSearchParams(window.location.search);
      const onboardingPath = path === '/onboarding';
      const onboardingHash = hash.includes('onboarding');
      setIsOnboardingRoute(onboardingPath || onboardingHash);
      const forced = params.has('onboarding') || params.get('forceOnboarding') === '1';
      setForceOnboarding(forced);
      if (onboardingPath && !onboardingHash) {
        try {
          window.location.hash = '#onboarding';
        } catch (error) {
          // Silently catch security errors
          console.warn('Could not update hash to onboarding:', error);
        }
      }
    };
    updateOnboardingRoute();
    window.addEventListener('hashchange', updateOnboardingRoute);
    window.addEventListener('popstate', updateOnboardingRoute);
    // Also listen for custom navigation events
    const handleNavigation = () => {
      updateOnboardingRoute();
    };
    window.addEventListener('navigateToOnboarding', handleNavigation);
    return () => {
      window.removeEventListener('hashchange', updateOnboardingRoute);
      window.removeEventListener('popstate', updateOnboardingRoute);
      window.removeEventListener('navigateToOnboarding', handleNavigation);
    };
  }, []);

  // Listen for custom navigation events to prevent redirect guard from interfering
  useEffect(() => {
    const handleNavigateToOnboarding = () => {
      // Set navigating flag FIRST to prevent guard from interfering
      navigatingRef.current = true;
      hasRedirectedRef.current = true; // Also mark as redirected to prevent guard
      // Immediately update hash state to trigger re-render
      setCurrentHashState('#onboarding');
      setIsOnboardingRoute(true);
      // Reset after navigation completes (longer delay to ensure navigation happens)
      setTimeout(() => {
        navigatingRef.current = false;
        // Keep hasRedirected true for a bit longer to prevent immediate re-redirect
        setTimeout(() => {
          hasRedirectedRef.current = false;
        }, 2000);
      }, 1500);
    };

    window.addEventListener('navigateToOnboarding', handleNavigateToOnboarding);
    return () => {
      window.removeEventListener('navigateToOnboarding', handleNavigateToOnboarding);
    };
  }, []);

  const adminEmails = useMemo(() => [
    'info@superkonnected.nl', 
    'support@superkonnected.nl',
    'bussinesssecurit@gmail.com',
    'businesssecurit@gmail.com',
    'bussinesssecurity@gmail.com',
    'businesssecurity@gmail.com',
    'info@business-security.nl'
  ], []);

  // Debug functie om admin status te controleren
  const checkAdminStatus = (email: string) => {
    const isAdmin = adminEmails.includes(email.toLowerCase());

    return isAdmin;
  };


  // Test Firebase connectie
  const testFirebaseConnection = async () => {
    try {
      console.log('🔍 Testing Firebase connection...');

      // Test Firestore connectie
      const { collection, getDocs, limit, query } = await import('firebase/firestore');
      const testQuery = query(collection(db, 'userProfiles'), limit(1));
      const snapshot = await getDocs(testQuery);
      console.log('✅ Firestore connectie werkt! Documenten gevonden:', snapshot.size);

      // Test Auth connectie
      console.log('✅ Auth connectie werkt! Current user:', auth.currentUser?.email || 'Geen gebruiker');

      // Test Storage connectie
      const { ref, listAll } = await import('firebase/storage');
      const storageRef = ref(storage);
      const result = await listAll(storageRef);
      console.log('✅ Storage connectie werkt! Items gevonden:', result.items.length);

    } catch (error: any) {
      console.error('❌ Firebase connectie error:', error.code, error.message);
      console.error('Error details:', error);
    }
  };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔄 Auth state changed:', user ? user.email : 'No user');
      setUser(user);

      if (user) {
        console.log('👤 User authenticated:', {
          email: user.email,
          provider: user.providerData[0]?.providerId,
          isNewUser: user.metadata.creationTime === user.metadata.lastSignInTime
        });

        // ── Toegangscontrole: admins mogen altijd binnen; overige users via allowedUsers ──────
        try {
          const { doc: fsDoc, getDoc: fsGetDoc } = await import('firebase/firestore');
          const emailKey = (user.email || '').toLowerCase();
          const isKnownAdmin = adminEmails.includes(emailKey);
          const allowedSnap = isKnownAdmin
            ? null
            : await fsGetDoc(fsDoc(db, 'allowedUsers', emailKey));
          if (!isKnownAdmin && !allowedSnap?.exists()) {
            console.warn('🚫 Toegang geweigerd voor:', emailKey);
            const blockedEmail = user.email || '';
            const { signOut: fbSignOut } = await import('firebase/auth');
            await fbSignOut(auth);
            setDeniedEmail(blockedEmail);
            setLoading(false);
            return;
          }
          console.log(isKnownAdmin ? '✅ Admin toegang toegestaan voor:' : '✅ Toegang toegestaan voor:', emailKey);
          setDeniedEmail(null);
          try {
            sessionStorage.removeItem('skGoogleLoginStarted');
          } catch {}
        } catch (err) {
          console.error('❌ Toegangscontrole mislukt:', err);
          // Bij een Firestore-fout toch blokkeren (fail closed)
          const blockedEmail = user.email || '';
          const { signOut: fbSignOut } = await import('firebase/auth');
          await fbSignOut(auth);
          setDeniedEmail(blockedEmail);
          setLoading(false);
          return;
        }
        // ─────────────────────────────────────────────────────────────────────

        // NIEUW: Track login for expert status
        try {
          await trackLogin(user.uid);
        } catch (error) {
          console.error('Error tracking login:', error);
          // Non-critical, don't block login
        }

        // Reset guard flags on new login
        hasRedirectedRef.current = false;
        guardInitializedRef.current = false;
        redirectingRef.current = false;
        navigatingRef.current = false;

        // Direct admin check op basis van email (zonder user profile dependency)
        const isUserAdmin = adminEmails.includes((user.email || '').toLowerCase());
        setIsAdmin(isUserAdmin);

        if (isUserAdmin) {
          console.log('👑 Admin user detected');
          // Admin kan kiezen tussen admin en member view
          setViewMode(viewMode); // Behoud huidige viewMode voor admins
        } else {
          console.log('👤 Regular user detected');
          // Niet-admins kunnen alleen member view zien
          setViewMode('member');
        }

        // Mark user online for dashboard visibility
        try {
          const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
          await setDoc(doc(db, 'userProfiles', user.uid), {
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || '',
            isOnline: true,
            lastSeen: serverTimestamp()
          }, { merge: true });
        } catch (e) {
          console.error('❌ Failed to mark user online:', e);
        }

        // Load user profile for onboarding check
        console.log('📥 Loading user profile...');
        await loadUserProfile(user);

        // Check for pending planning sessions and show reminder toast
        try {
          const { collection: fsCollection, query: fsQuery, where: fsWhere, getDocs: fsGetDocs, limit: fsLimit } = await import('firebase/firestore');
          const pendingQ = fsQuery(
            fsCollection(db, 'inboxItems'),
            fsWhere('userId', '==', user.uid),
            fsWhere('type', '==', 'planning_pending'),
            fsWhere('isRead', '==', false),
            fsLimit(1)
          );
          const pendingSnap = await fsGetDocs(pendingQ);
          if (!pendingSnap.empty) {
            const pendingDoc = pendingSnap.docs[0];
            const pendingData = pendingDoc.data();
            const otherName = pendingData.data?.otherUserName || pendingData.fromUserName || 'je connectie';
            const cId = pendingData.data?.connectionId || pendingData.connectionId;
            if (cId) {
              setTimeout(() => {
                setPendingPlanningToast({ connectionId: cId, otherUserName: otherName, inboxItemId: pendingDoc.id });
              }, 2500); // kleine vertraging zodat dashboard eerst laadt
            }
          }
        } catch (e) {
          console.warn('Could not check pending planning sessions', e);
        }
      } else {
        console.log('🚪 User signed out');
        try {
          if (sessionStorage.getItem('skGoogleLoginStarted') === '1' && !redirectResultCheckedRef.current) {
            console.log('⏳ Waiting for Google redirect result before treating user as signed out');
            return;
          }
          if (sessionStorage.getItem('skGoogleLoginStarted') === '1') {
            sessionStorage.removeItem('skGoogleLoginStarted');
          }
        } catch {}
        setIsAdmin(false);
        setViewMode('member');
        setUserProfile(null);
      }

      setLoading(false);
    });

    // Dev helper uitgeschakeld: auto-testaccounts veroorzaakten sign-in/sign-out bounces
    // Als je dit ooit handmatig wilt activeren: localStorage.setItem('ALLOW_TEST_SEED','1')
    // if (
    //   process.env.NODE_ENV !== 'production' &&
    //   window.location.hostname === 'localhost' &&
    //   localStorage.getItem('ALLOW_TEST_SEED') === '1'
    // ) {
    //   createTestAccountsIfNeeded();
    // }

    return () => unsubscribe();
  }, [adminEmails, viewMode]);

  // Reset login state when user is null (logged out)
  // BUT: Don't reset during OAuth callback to prevent redirect loops
  useEffect(() => {
    // Check if we're in an OAuth callback flow (before state updates)
    const isOAuthCallback = window.location.pathname.includes('/oauth/linkedin/callback') ||
                           window.location.pathname.includes('/oauth/facebook/callback') ||
                           window.location.pathname.includes('/oauth/twitter/callback') ||
                           window.location.pathname.includes('/outlook-callback');
    
    if (!user && !showOAuthCallback && !isOAuthCallback) {
      console.log('User is null, resetting login state');
      setIsLoggingIn(false);
    }
  }, [user, showOAuthCallback]);


  // ✅ Real-time listener for userProfile updates
  useEffect(() => {
    if (!user?.uid) return;
    
    const userRef = doc(db, 'userProfiles', user.uid);
    
    
    const unsubscribe = onSnapshot(userRef, (snapshot: any) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        // Compute displayName from firstName + lastName if not directly available
        const firstName = data.firstName || data.onboardingData?.firstName || '';
        const lastName = data.lastName || data.onboardingData?.lastName || '';
        const computedDisplayName = data.displayName || data.onboardingData?.displayName || `${firstName} ${lastName}`.trim() || user.displayName || '';
        
        // Update userProfile state with new data
        setUserProfile(prev => ({
          ...prev,
          ...cleanBlobUrls(data as any),
          uid: user.uid,
          displayName: computedDisplayName,
          email: user.email || data.email || '',
          photoURL: user.photoURL || data.photoURL || '',
          photoPosition: data.photoPosition || data.onboardingData?.photoPosition,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone || data.phoneNumber,
          phoneNumber: data.phoneNumber || data.phone,
          company: data.company,
          companyLocation: data.companyLocation,
          onboardingData: data.onboardingData || {}
        }));
      }
    }, (error: any) => {
      console.error('❌ Error in userProfile real-time listener:', error);
    });
    
    return () => {
      console.log('🔌 Unsubscribing from userProfile listener');
      unsubscribe();
    };
  }, [user?.uid]);

  // ob-fix: Single source of truth route guards with admin bypass
  useEffect(() => {
    if (user) {
      const onboardingRoute = isOnboardingRoute;

      // Admin bypass - admins skip onboarding entirely unless forced
      if (isAdmin) {
        if (onboardingRoute && !forceOnboarding) {
          try {
            window.location.hash = '#dashboard';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect admin to dashboard:', error);
          }
        }
        return;
      }

      // Regular users need userProfile loaded
      if (!userProfile) {
        return;
      }

      // Get current hash directly from window.location - always use fresh value
      const currentHash = window.location.hash || '';
      const isOnOnboardingPage = currentHash.includes('onboarding') || onboardingRoute;
      
      // CRITICAL: If user is already on onboarding page, NEVER redirect them away
      // This prevents the guard from interfering while user is filling out the form
      if (isOnOnboardingPage || currentHash === '#onboarding') {
        redirectingRef.current = false;
        hasRedirectedRef.current = true; // Mark that we're on onboarding
        guardInitializedRef.current = true;
        return; // User is on onboarding - let them stay, don't interfere!
      }
      
      // Skip redirect if user is actively navigating or we're already redirecting
      if (navigatingRef.current || redirectingRef.current) {
        // Reset navigating flag after a short delay
        if (navigatingRef.current) {
          setTimeout(() => {
            navigatingRef.current = false;
          }, 500);
        }
        return;
      }

      // Check if all required onboarding fields are completed
      const requiredFieldsCheck = hasCompletedRequiredOnboardingFields(userProfile);

      // Allow a short "just completed" window to prevent bounce-back after Ga Live.
      let justCompleted = false;
      try {
        const raw = sessionStorage.getItem('onboardingJustCompletedAt');
        const ts = raw ? Number(raw) : 0;
        justCompleted = Number.isFinite(ts) && ts > 0 && (Date.now() - ts) < 2 * 60 * 1000;
      } catch {
        justCompleted = false;
      }
      
      // Check onboarding status - new format has priority
      const isOnboarded = userProfile.onboarding?.status === 'done' || userProfile.isOnboarded || justCompleted;
      
      // Gate on explicit onboarding completion; required fields stay diagnostic.
      const isFullyOnboarded = isOnboarded;

      // DEBUG: Log onboarding check (always log for debugging)
      console.log('🔍 Onboarding Guard Check:', {
        email: user?.email,
        isOnboarded,
        requiredFieldsCheck,
        isFullyOnboarded,
        currentHash,
        isOnOnboardingPage,
        activePage,
        guardInitialized: guardInitializedRef.current,
        hasRedirected: hasRedirectedRef.current,
        missingFields: requiredFieldsCheck.missingFields,
        userProfile: {
          onboarding: userProfile.onboarding,
          isOnboarded: userProfile.isOnboarded,
          displayName: userProfile.displayName,
          email: userProfile.email,
          phone: userProfile.phone,
          phoneNumber: userProfile.phoneNumber,
          company: userProfile.company,
          companyLocation: userProfile.companyLocation,
          location: userProfile.location,
          postcode: userProfile.postcode,
          city: userProfile.city,
          workScope: userProfile.workScope,
          region: userProfile.region,
          expertise: userProfile.expertise,
          interests: userProfile.interests
        }
      });

      // Update userProfile in memory if onboarding is done but flag is false
      if (userProfile.onboarding?.status === 'done' && !userProfile.isOnboarded && requiredFieldsCheck.isComplete) {
        setUserProfile(prev => prev ? { ...prev, isOnboarded: true } : prev);

        // Also save to database permanently
        if (user) {
          import('firebase/firestore').then(({ doc, updateDoc }) => {
            updateDoc(doc(db, 'userProfiles', user.uid), {
              isOnboarded: true,
              'onboarding.status': 'done',
              'onboarding.completedAt': Date.now()
            }).then(() => {
              console.log('✅ Onboarding status saved to database');
            }).catch((error) => {
              console.error('❌ Error saving onboarding status:', error);
            });
          });
        }
      }

      // Guard: Redirect to onboarding if not fully onboarded
      // Only redirect if user is NOT already on onboarding page (to prevent loops)
      // AND user is NOT actively navigating to onboarding
      if (!isFullyOnboarded && !isOnOnboardingPage && !navigatingRef.current) {
        // Mark guard as initialized
        if (!guardInitializedRef.current) {
          guardInitializedRef.current = true;
        }
        
        // Only redirect if we haven't redirected recently (prevent loops)
        if (!hasRedirectedRef.current) {
          hasRedirectedRef.current = true;
          redirectingRef.current = true;
          
          // Always log redirect details
          console.log('🔍 Redirecting to onboarding - not fully onboarded', {
            email: user?.email,
            isOnboarded,
            requiredFieldsCheck,
            isFullyOnboarded,
            currentHash,
            isOnOnboardingPage,
            missingFields: requiredFieldsCheck.missingFields
          });
          
          try {
            window.location.hash = '#onboarding';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect to onboarding:', error);
          }
          setTimeout(() => {
            redirectingRef.current = false;
            // Reset hasRedirected after a delay to allow re-checking if needed
            setTimeout(() => {
              hasRedirectedRef.current = false;
            }, 1000);
          }, 200);
          return;
        }
      }

      // Guard: If fully onboarded and on onboarding page, redirect to dashboard (unless forced)
      // Only redirect if we're not already on the dashboard to prevent flickering
      if (isFullyOnboarded && isOnOnboardingPage && !forceOnboarding) {
        const isOnDashboard = currentHash === '#dashboard' || currentHash === '' || (!currentHash && activePage === 'dashboard');
        if (!isOnDashboard) {
          redirectingRef.current = true;
          try {
            window.location.hash = '#dashboard';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect to dashboard:', error);
          }
          setTimeout(() => {
            redirectingRef.current = false;
          }, 200);
          return;
        }
      }
      
      // Reset redirect flag if we're on the correct page
      redirectingRef.current = false;

      // Check for pending connection requests and redirect to ConnectieFlow if needed
      if (isFullyOnboarded && user?.uid && activePage !== 'connecties') {
        const checkPendingRequests = async () => {
          try {
            const { connectionRequestService } = await import('./services/connectionRequestService');
            const receivedRequests = await connectionRequestService.getReceivedConnectionRequests(user.uid);
            const pendingReceived = receivedRequests.filter(req => req.status === 'pending');
            
            if (pendingReceived.length > 0 && !localStorage.getItem('pendingRequestsRedirected')) {
              console.log('🔔 Pending connection requests found, redirecting to ConnectieFlow...', pendingReceived.length);
              localStorage.setItem('pendingRequestsRedirected', 'true');
              // Clear flag after 5 minutes to allow re-checking
              setTimeout(() => {
                localStorage.removeItem('pendingRequestsRedirected');
              }, 5 * 60 * 1000);
              
              // Redirect to connecties page (which shows ConnectieFlow)
              if (currentHash !== '#connecties') {
                try {
                  window.location.hash = '#connecties';
                  setActivePage('connecties');
                } catch (error) {
                  console.warn('Could not redirect to connecties:', error);
                }
              }
            }
          } catch (error) {
            console.error('Error checking pending requests:', error);
          }
        };
        
        // Only check once per session to avoid loops
        if (!sessionStorage.getItem('pendingRequestsChecked')) {
          sessionStorage.setItem('pendingRequestsChecked', 'true');
          setTimeout(checkPendingRequests, 2000); // Wait 2 seconds after profile loads
        }
      }
    } else {
      // Reset flags when user logs out
      hasRedirectedRef.current = false;
      guardInitializedRef.current = false;
    }
  }, [user, userProfile, isAdmin, isOnboardingRoute, forceOnboarding, activePage]);

  const loadUserProfile = async (user: User) => {
    try {
      console.log('🔄 Loading user profile for:', user.email);

      // Admin accounts get minimal profile without Firestore dependency
      const isUserAdmin = adminEmails.includes((user.email || '').toLowerCase());
      if (isUserAdmin) {
        console.log('👑 Admin user detected, creating minimal profile');
        const adminProfile: UserProfile = {
          uid: user.uid,
          displayName: user.displayName || 'SuperKonnected Admin',
          email: user.email || '',
          photoURL: user.photoURL || '',
          isOnboarded: true, // Admins are always "onboarded"
          points: 0,
          badges: [],
          joinDate: new Date(),
          socialSharingEnabled: false,
          sharingFrequency: 3,
          totalShares: 0,
          connectionStatus: 'none'
        };
        setUserProfile(adminProfile);
        // Force admin to dashboard
        if (!window.location.hash.includes('dashboard')) {
          try {
            window.location.hash = '#dashboard';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect admin to dashboard:', error);
          }
        }
        return;
      }

      console.log('📡 Connecting to Firestore...');
      const { getDocs, query, collection, where, getDoc, setDoc, doc, serverTimestamp } = await import('firebase/firestore');

      // ob-fix: First try direct document access (new format), then fallback to query (old format)
      console.log('🔍 Trying direct document access for UID:', user.uid);

      // Try direct document access first (UID as document ID)
      const directDocRef = doc(db, 'userProfiles', user.uid);
      const directDocSnapshot = await getDoc(directDocRef);

      let userSnapshot;
      let foundData = null;

      if (directDocSnapshot.exists()) {
        console.log('✅ Found user profile in direct document');
        foundData = directDocSnapshot.data();
      } else {
        console.log('🔍 Direct document not found, trying query fallback...');
        // Fallback to query by uid field (old format)

        const userQuery = query(collection(db, 'userProfiles'), where('uid', '==', user.uid));
        userSnapshot = await getDocs(userQuery);
        if (!userSnapshot.empty) {
          console.log('✅ Found user profile via query');
          foundData = userSnapshot.docs[0].data();
        } else {
          console.log('❌ No user profile found in Firestore');
        }
      }

      if (foundData) {
        console.log('✅ Found user profile, processing...');
        const data = foundData as UserProfile;

        // ob-fix: Auto-complete onboarding for existing accounts that can login
        const needsUpgrade = !data.firstName || !data.phone || !data.address;
        const isOldAccount = !data.onboarding || data.onboarding.status !== 'done';

        if (needsUpgrade || isOldAccount) {
          console.log('🔄 Upgrading existing profile...');
          const upgradedProfile = {
            ...data,
            // Add any missing core fields
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            phoneNumber: data.phoneNumber || '',
            address: data.address || '',
            street: data.street || '',
            city: data.city || '',
            postalCode: data.postalCode || '',
            zipCode: data.zipCode || '',
            state: data.state || '',
            province: data.province || '',
            jobTitle: data.jobTitle || '',
            position: data.position || '',
            role: data.role || '',
            website: data.website || '',
            portfolio: data.portfolio || '',
            timezone: data.timezone || '',
            birthDate: data.birthDate || '',
            age: data.age || '',

            // ob-fix: Auto-complete onboarding for existing accounts
            isOnboarded: true,
            onboarding: {
              status: 'done',
              completedAt: Date.now()
            },
            updatedAt: new Date()
          };

          // Update in Firestore
          await setDoc(doc(db, 'userProfiles', user.uid), upgradedProfile, { merge: true });

          // Update local state immediately
          data.isOnboarded = true;
          data.onboarding = { status: 'done', completedAt: Date.now() };
        }

        // Merge all onboarding data into finalProfile
        // Include ALL fields from data to ensure completion check works
        // Compute displayName from firstName + lastName if not directly available
        const firstName = data.firstName || data.onboardingData?.firstName || '';
        const lastName = data.lastName || data.onboardingData?.lastName || '';
        const computedDisplayName = data.displayName || data.onboardingData?.displayName || `${firstName} ${lastName}`.trim() || user.displayName || '';
        
        const finalProfile = {
          ...cleanBlobUrls(data as any),
          uid: user.uid,
          displayName: computedDisplayName,
          email: user.email || data.email || '',
          photoURL: user.photoURL || data.photoURL || '',
          photoPosition: data.photoPosition || data.onboardingData?.photoPosition,
          isOnboarded: data.isOnboarded || false,
          points: data.points || 0,
          badges: data.badges || [],
          socialSharingEnabled: data.socialSharingEnabled || false,
          sharingFrequency: data.sharingFrequency || 3,
          totalShares: data.totalShares || 0,
          connectionStatus: data.connectionStatus || 'none',
          // Include all onboarding fields for completion check (direct from data)
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone || data.phoneNumber,
          phoneNumber: data.phoneNumber || data.phone,
          company: data.company,
          companyLocation: data.companyLocation,
          location: data.location,
          postcode: data.postcode,
          city: data.city,
          workScope: data.workScope,
          region: data.region,
          expertise: data.expertise,
          interests: data.interests,
          // Also check onboardingData object if it exists (nested structure)
          onboardingData: data.onboardingData || {}
        };

        setUserProfile(finalProfile);
        console.log('✅ User profile loaded successfully:', finalProfile);
        const computedOnboarded = finalProfile.onboarding?.status === 'done' || finalProfile.isOnboarded;
        if (!computedOnboarded && !window.location.hash.includes('onboarding')) {
          try {
            window.location.hash = '#onboarding';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect to onboarding:', error);
          }
        } else if (computedOnboarded && window.location.hash.includes('onboarding')) {
          try {
            window.location.hash = '#dashboard';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect to dashboard:', error);
          }
        }
      } else {
        console.log('📝 Creating new user profile in Firestore...');

        // Create minimal profile for new users
        const newProfile: UserProfile = {
          uid: user.uid,
          displayName: user.displayName || '',
          email: user.email || '',
          photoURL: user.photoURL || '',
          isOnboarded: false,
          points: 0,
          badges: [],
          joinDate: new Date(),
          socialSharingEnabled: false,
          sharingFrequency: 3,
          totalShares: 0,
          connectionStatus: 'none'
        };

        // Save minimal profile to Firestore
        console.log('💾 Saving new profile to Firestore...');
        await setDoc(doc(db, 'userProfiles', user.uid), newProfile);
        console.log('✅ New profile saved to Firestore successfully');

        setUserProfile(newProfile);
        console.log('✅ New user profile created and loaded:', newProfile);
        // New users go straight to onboarding
        if (!window.location.hash.includes('onboarding')) {
          try {
            window.location.hash = '#onboarding';
          } catch (error) {
            // Silently catch security errors
            console.warn('Could not redirect new user to onboarding:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        }
      });

      console.log('⚠️ Using fallback profile due to error');
      // Fallback profile - vooral belangrijk voor test account
      const fallbackProfile: UserProfile = {
        uid: user.uid,
        displayName: user.displayName || '',
        email: user.email || '',
        photoURL: user.photoURL || '',
        isOnboarded: false,
        points: 0,
        badges: [],
        joinDate: new Date(),
        socialSharingEnabled: false,
        sharingFrequency: 3,
        totalShares: 0,
        connectionStatus: 'none'
      };

      console.log('🔄 Setting fallback profile:', fallbackProfile);
      setUserProfile(fallbackProfile);
    }
  };


  const signInWithLinkedIn = async () => {
    console.log('🔗 Starting LinkedIn login with custom OAuth...');
    
    // Set loading state
    setIsLoggingIn(true);
    
    // Use our existing LinkedIn OAuth service for login
    // This gives us more control and uses the same flow as social sharing
    try {
      const { linkedinOAuth } = await import('./services/oauth/linkedinOAuth');
      
      // Store that this is a login flow (not just social sharing)
      sessionStorage.setItem('linkedinLoginFlow', 'true');
      
      // Start OAuth flow - will redirect to LinkedIn
      linkedinOAuth.startOAuthFlow();
      
      // Note: After successful OAuth, user will be redirected back to our callback
      // The callback will handle creating the Firebase user account
      
    } catch (error) {
      console.error('❌ LinkedIn login error:', error);
      alert('LinkedIn login mislukt. Probeer het opnieuw.');
      setIsLoggingIn(false);
    }
  };


  // ── Mobile helpers ───────────────────────────────────────────────────────────
  // Compute user initials for the MobileTopBar avatar
  const userInitials = useMemo(() => {
    const name = (userProfile?.displayName || user?.displayName || '').trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts[0]?.length >= 2) return parts[0].slice(0, 2).toUpperCase();
    return 'SK';
  }, [user, userProfile]);

  // Cycle through the sub-tabs of the current page (List button in MobileTopBar)
  const handleMobileListClick = () => {
    const tabs = MOBILE_SUB_TABS[activePage] || [];
    if (tabs.length > 0) setMobileSubTabIdx(prev => (prev + 1) % tabs.length);
  };

  // Reset sub-tab index whenever the active page changes
  useEffect(() => {
    setMobileSubTabIdx(0);
  }, [activePage]);
  // ── End mobile helpers ───────────────────────────────────────────────────────

  const handleSignOut = async () => {
    console.log('🚪 Uitloggen gestart...');
    try {
      await signOut(auth);
      console.log('✅ Uitgelogd via Firebase');
      // Force page reload om zeker te zijn dat alles wordt gereset
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Error signing out:', error);
      // Fallback: forceer reload
      window.location.reload();
    }
  };

  // ⚠️ CRITICAL: OAuth callback must render BEFORE !user check
  // Otherwise user will be redirected to login during OAuth flow
  
  // Handle Outlook OAuth callback
  if (window.location.pathname.includes('/outlook-callback') || window.location.pathname === '/outlook-callback') {
    console.log('🔵 Outlook callback detected');
    return (
      <div className="fixed inset-0 z-[9999] bg-white">
        <OutlookCallback />
      </div>
    );
  }
  
  if (showOAuthCallback || window.location.pathname.includes('/oauth/linkedin/callback')) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white">
        <LinkedInCallback />
      </div>
    );
  }
  
  // Handle Twitter/X OAuth callback
  if (window.location.pathname.includes('/oauth/twitter/callback')) {
    console.log('🐦 Twitter callback detected');
    return (
      <div className="fixed inset-0 z-[9999] bg-white">
        <TwitterCallback />
      </div>
    );
  }
  
  // Handle Pinterest OAuth callback
  if (window.location.pathname.includes('/oauth/pinterest/callback')) {
    console.log('📌 Pinterest callback detected');
    return (
      <div className="fixed inset-0 z-[9999] bg-white">
        <PinterestCallback />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center w-64 h-64 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#7A9488] mx-auto mb-4"></div>
          <p className="text-black text-lg">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user && deniedEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f8f6] px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Geen toegang</h2>
          <p className="text-gray-500 mb-1 text-sm">
            Het account <span className="font-medium text-gray-700">{deniedEmail}</span> heeft nog geen toegang tot de app.
          </p>
          <p className="text-gray-400 text-sm mb-6">
            Neem contact op met de beheerder om toegang te krijgen.
          </p>
          <button
            onClick={() => setDeniedEmail(null)}
            className="w-full py-2.5 rounded-xl bg-[#8BA59B] hover:bg-[#7A9488] text-white font-medium transition-colors"
          >
            Terug naar inloggen
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    const publicRoute = typeof window !== 'undefined'
      ? (window.location.hash.slice(1).split('/')[0] || window.location.pathname.replace(/^\/+|\/+$/g, ''))
      : '';
    if (publicRoute === 'terms') {
      return (
        <>
          {showOobHandler && (
            <OobActionHandler onClose={() => setShowOobHandler(false)} />
          )}
          <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.location.hash = ''; }}
              className="text-[#8BA59B] hover:text-[#7A9488] font-medium text-sm"
            >
              ← Terug naar inloggen
            </a>
          </div>
          <TermsOfService />
        </>
      );
    }
    if (publicRoute === 'privacy') {
      return (
        <>
          {showOobHandler && (
            <OobActionHandler onClose={() => setShowOobHandler(false)} />
          )}
          <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.location.hash = ''; }}
              className="text-[#8BA59B] hover:text-[#7A9488] font-medium text-sm"
            >
              ← Terug naar inloggen
            </a>
          </div>
          <PrivacyPolicy />
        </>
      );
    }
    return (
      <>
        {showOobHandler && (
          <OobActionHandler onClose={() => setShowOobHandler(false)} />
        )}
        <LoginPage />
      </>
    );
  }

  // Check if we should show onboarding - check both state and current hash for immediate response
  const currentHashForOnboarding = currentHashState || (typeof window !== 'undefined' ? window.location.hash : '');
  const shouldShowOnboarding = user && (
    isOnboardingRoute || 
    forceOnboarding || 
    currentHashForOnboarding.includes('onboarding')
  );
  
  if (shouldShowOnboarding) {
    return (
      <>
        {showOobHandler && (
          <OobActionHandler onClose={() => setShowOobHandler(false)} />
        )}
        <OnboardingWizard userProfile={user} />
      </>
    );
  }

  // Check if user needs onboarding (redirect happens in useEffect)
  if (user && userProfile && !userProfile.isOnboarded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-black text-lg">Redirecting naar onboarding...</p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <ExperienceModeProvider>
        <ErrorBoundary>
        {profileSubPage === 'english' ? <SeoEn /> : <Seo />}
        
        {/* Beta Badge - Always visible */}
        <BetaBadge />
        
        <div className="min-h-screen bg-white" id="app-root">
        {showOobHandler && (
          <OobActionHandler onClose={() => setShowOobHandler(false)} />
        )}

        {/* Global Call Popup tijdelijk uitgezet i.v.m. Google Meet integratie */}
        {/* <GlobalCallPopup isInActiveChatFlow={activePage === 'chat-system' || activePage === 'chat'} /> */}
        {/* <DebugFlags /> */}
        {/* Force cache bust - v2 */}
        
        {/* ── Mobile Top Bar ── only visible below md (768px) */}
        {user && (
          <MobileTopBar
            activePage={activePage}
            pageTitle={MOBILE_PAGE_TITLES[activePage] || 'SuperKonnected'}
            userInitials={userInitials}
            onListClick={handleMobileListClick}
            onAvatarClick={() => setShowProfileMenu(!showProfileMenu)}
            dimmed={mobileNavDimmed}
          />
        )}

        {/* ── Mobile Profile Dropdown ── fixed below MobileTopBar, mobile only */}
        {showProfileMenu && (
          <>
            {/* Backdrop */}
            <div
              className="md:hidden fixed inset-0 z-[9988]"
              onClick={() => setShowProfileMenu(false)}
            />
            {/* Dropdown */}
            <div
              data-profile-menu
              className="md:hidden fixed top-[52px] left-1/2 w-[calc(100vw-24px)] max-w-[360px] -translate-x-1/2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[9989]"
            >
              {canShowPage('mijn-verhaal') && (
                <button onClick={() => { setActivePage('mijn-verhaal'); setProfileSubPage(null); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                  <ProfileIcon size={18} weight="regular" className="text-[#7A9488]" />
                  <span>Mijn Verhaal</span>
                </button>
              )}
              <button onClick={() => { setActivePage('dashboard'); setProfileSubPage('profile-hub'); setShowProfileMenu(false); window.location.hash = '#profile-hub'; }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                <HomeIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Mijn Profiel</span>
              </button>
              <button
                onClick={() => {
                  setActivePage('dashboard');
                  setProfileSubPage('profile-hub');
                  setShowProfileMenu(false);
                  window.location.hash = '#profile-hub';
                  setTimeout(() => {
                    document.getElementById('instellingen')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 150);
                }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3"
              >
                <SettingsIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Instellingen</span>
              </button>
              <button
                onClick={() => {
                  setActivePage('dashboard');
                  setProfileSubPage('profile-hub');
                  setShowProfileMenu(false);
                  window.location.hash = '#profile-hub';
                  setTimeout(() => {
                    document.getElementById('mobile-weergave-filters')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 150);
                }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-[rgba(122,148,136,0.08)] transition-colors text-[#4F6660] flex items-center gap-3"
              >
                <FiltersIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Weergave &amp; Filters</span>
              </button>
              {user && (
                <button onClick={() => { setActivePage('dashboard'); setProfileSubPage('llm-dashboard'); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                  <InsightIcon size={18} weight="regular" className="text-[#7A9488]" />
                  <span>LLM Expertstatus</span>
                </button>
              )}
              <button onClick={() => { setActivePage('dashboard'); setProfileSubPage('membership'); setShowProfileMenu(false); window.location.hash = '#membership'; }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                <PremiumIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Lidmaatschap</span>
              </button>
              <button onClick={() => { setActivePage('abonnement'); setProfileSubPage(null); setShowProfileMenu(false); window.location.hash = '#abonnement'; }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                <PremiumIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Abonnementen</span>
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{subscription?.tier === 'premium' ? 'Premium' : 'Gratis'}</span>
              </button>
              <button onClick={() => { setProfileSubPage('groepsbeheer'); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                <CommunityIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Mijn Groepen</span>
              </button>
              <button onClick={() => { setShowCreditsInfo(true); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3">
                <PremiumIcon size={18} weight="regular" className="text-[#7A9488]" />
                <span>Credits</span>
                <span className="ml-auto text-xs text-gray-400">{creditsBalance ?? 0}</span>
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button onClick={() => { setShowProfileMenu(false); handleSignOut(); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 transition-colors text-red-600 flex items-center gap-3">
                <NextIcon size={18} weight="regular" className="text-red-500" />
                <span>Uitloggen</span>
              </button>
            </div>
          </>
        )}

        {/* ── Mobile Sub-tabs ── rendered at App level, sticky below MobileTopBar */}
        {user && (
          <PageTabs
            tabs={(MOBILE_SUB_TABS[activePage] || []).map((label, i) => ({
              id: String(i),
              label,
              active: mobileSubTabIdx === i,
              onClick: () => setMobileSubTabIdx(i),
            }))}
            topOffset={52}
            isDark={activePage === 'vibes'}
            dimmed={mobileNavDimmed}
          />
        )}

        {/* Main Navigation Header - Clean Mind Design (desktop only) */}
        <div className="desktop-main-nav hidden md:block sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <AutoFadeNavbar className="flex justify-between items-center h-16">
            {/* Left: Logo + Brand */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <img 
                  src="/SuperKonnected_Logo.svg" 
                  alt="SuperKonnected" 
                  className="h-7 w-auto"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
                <h1 className="text-base font-semibold tracking-tight text-gray-900 md:font-medium">
                  SuperKonnected
                </h1>
              </div>
              
              {/* Secondary Actions - Subtiel */}
              <div className="hidden lg:flex items-center gap-3 text-xs text-gray-500">
                <a 
                  href="https://superkonnected.nl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-gray-900 transition-colors"
                >
                  {profileSubPage === 'english' ? 'Main Site' : 'Hoofdsite'}
                </a>
                <span className="text-gray-300">•</span>
                <button
                  onClick={() => {
                    if (profileSubPage === 'english') {
                      setProfileSubPage(null);
                      window.location.hash = '#dashboard';
                    } else {
                      setProfileSubPage('english');
                      window.location.hash = '#en';
                    }
                  }}
                  className="hover:text-gray-900 transition-colors"
                >
                  {profileSubPage === 'english' ? 'NL' : 'EN'}
                </button>
              </div>
              
              {/* Goal Context Switcher */}
              {user && <GoalContextSwitcher userId={user.uid} />}
            </div>

            {/* Center: Main Navigation - Elegant */}
            <nav className="hidden md:flex items-center gap-1">
              <button
                onClick={() => {
                  setActivePage('dashboard');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activePage === 'dashboard' && !profileSubPage 
                    ? 'text-gray-900 bg-gray-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Home
              </button>
              
              {canShowPage('connecties') && (
              <button
                onClick={() => {
                  if (!canAccessPage('connecties')) return;
                  navigatingRef.current = true;
                  setActivePage('connecties');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                  if (hasMissedRequests) {
                    clearMissedRequests();
                  }
                }}
                disabled={!canAccessPage('connecties')}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  !canAccessPage('connecties')
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed opacity-50'
                    : activePage === 'connecties' 
                      ? 'text-gray-900 bg-gray-50' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                title={!canAccessPage('connecties') ? 'Geen toegang in huidige team context' : ''}
              >
                Netwerk
                {hasMissedRequests && canAccessPage('connecties') && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-[#7A9488] rounded-full"></span>
                )}
              </button>
              )}

              {canShowPage('agenda') && (
              <button
                onClick={() => {
                  if (!canAccessPage('agenda')) return;
                  setActivePage('agenda');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                disabled={!canAccessPage('agenda')}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  !canAccessPage('agenda')
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed opacity-50'
                    : activePage === 'agenda' 
                      ? 'text-gray-900 bg-gray-50' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                title={!canAccessPage('agenda') ? 'Geen toegang in huidige team context' : ''}
              >
                Agenda
                {canAccessPage('agenda') && activePage !== 'agenda' && upcomingAgendaCount > 0 && (
                  <InboxBadge hasUnread count={upcomingAgendaCount} />
                )}
              </button>
              )}
              
              {canShowPage('inbox') && (
              <button
                onClick={() => {
                  if (!canAccessPage('inbox')) return;
                  setActivePage('inbox');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                disabled={!canAccessPage('inbox')}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  !canAccessPage('inbox')
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed opacity-50'
                    : activePage === 'inbox' 
                      ? 'text-gray-900 bg-gray-50' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                title={!canAccessPage('inbox') ? 'Geen toegang in huidige team context' : ''}
              >
                Inbox
                {canAccessPage('inbox') && (
                  <InboxBadge hasUnread={hasUnreadInbox} count={inboxUnreadCount} />
                )}
              </button>
              )}

              {canShowPage('vibes') && (
              <button
                onClick={() => {
                  if (!canAccessPage('vibes')) return;
                  setActivePage('vibes');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                disabled={!canAccessPage('vibes')}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  !canAccessPage('vibes')
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed opacity-50'
                    : activePage === 'vibes' 
                      ? 'text-gray-900 bg-gray-50' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                title={!canAccessPage('vibes') ? 'Geen toegang in huidige team context' : ''}
              >
                Vibes
              </button>
              )}

              {/* LLM Expertstatus - verplaatst naar profiel dropdown menu */}

              {canShowPage('social-media') && (
              <button
                onClick={() => {
                  setActivePage('social-media');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activePage === 'social-media' 
                    ? 'text-gray-900 bg-gray-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Social
              </button>
              )}


              {/* Feeds tijdelijk uit nav verbergd */}
              {/*
              <button
                onClick={() => {
                  setActivePage('feeds');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                className={`relative group px-1.5 py-2 text-sm transition-colors ${
                  activePage === 'feeds' ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Externe feeds met Clean Mind filtering"
              >
                🌐 Feeds
                <span
                  aria-hidden
                  className={`pointer-events-none absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-[#7A9488] via-[#9AAF88] to-[#C5D3B8] transition-opacity ${
                    activePage === 'feeds' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                />
              </button>
              */}

              {/* Groups Navigation — visible when user is member/owner of any entity, or is test account */}
              {showGroupsTab && (
                <div className="relative" ref={entityDropdownRef}>
                  {userEntities.length === 0 && isTestAccount ? (
                    /* Test account without entities yet — direct link to mastermind hub */
                    <button
                      onClick={() => {
                        setActivePage('dashboard');
                        setProfileSubPage('mastermind-hub');
                        setShowProfileMenu(false);
                        window.location.hash = '#mastermind-hub';
                      }}
                      className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                        profileSubPage === 'mastermind-hub'
                          ? 'text-gray-900 bg-gray-50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <ThinkIcon size={16} weight="regular" className="shrink-0" />
                      <span>Mastermind</span>
                    </button>
                  ) : userEntities.length === 1 ? (
                    <button
                      onClick={() => {
                        const entity = userEntities[0];
                        const hubType = entity.type === 'networkclub' ? 'networkclub-hub' : 'mastermind-hub';
                        localStorage.setItem('activeEntityId', entity.id);
                        window.dispatchEvent(new CustomEvent('activeEntityChanged', { detail: { entityId: entity.id } }));
                        setActivePage('dashboard');
                        setProfileSubPage(hubType as any);
                        setShowProfileMenu(false);
                        window.location.hash = `#${hubType}`;
                      }}
                      className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                        (profileSubPage === 'networkclub-hub' || profileSubPage === 'mastermind-hub')
                          ? 'text-gray-900 bg-gray-50' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <CommunityIcon size={16} weight="regular" className="shrink-0" />
                      <span className="max-w-[120px] truncate">{userEntities[0].name}</span>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowEntityDropdown(!showEntityDropdown)}
                        className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                          (profileSubPage === 'networkclub-hub' || profileSubPage === 'mastermind-hub')
                            ? 'text-gray-900 bg-gray-50' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <CommunityIcon size={16} weight="regular" className="shrink-0" />
                        <span>Groups</span>
                        <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${showEntityDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showEntityDropdown && (
                        <div className="absolute top-full left-0 mt-1.5 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-50">
                          {userEntities.map((entity) => {
                            const TypeIcon = entity.type === 'mastermind' ? ThinkIcon
                              : entity.type === 'community' ? CommunityIcon
                              : PartnerIcon;
                            const typeLabel = entity.type === 'networkclub' ? 'Netwerkclub'
                              : entity.type === 'mastermind' ? 'Mastermind'
                              : 'Community';
                            return (
                              <button
                                key={entity.id}
                                onClick={() => {
                                  const hubType = entity.type === 'networkclub' ? 'networkclub-hub' : 'mastermind-hub';
                                  localStorage.setItem('activeEntityId', entity.id);
                                  window.dispatchEvent(new CustomEvent('activeEntityChanged', { detail: { entityId: entity.id } }));
                                  setActivePage('dashboard');
                                  setProfileSubPage(hubType as any);
                                  setShowProfileMenu(false);
                                  setShowEntityDropdown(false);
                                  window.location.hash = `#${hubType}`;
                                }}
                                className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-3 rounded-lg mx-1"
                                style={{ width: 'calc(100% - 8px)' }}
                              >
                                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                  <TypeIcon size={16} weight="regular" className="text-gray-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 truncate">{entity.name}</div>
                                  <div className="text-xs text-gray-500">{typeLabel}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Admin-only quick access tabs */}
              {isAdmin && viewMode === 'admin' && (
                <>
                  <button
                    onClick={() => {
                      setActivePage('dashboard');
                      setProfileSubPage('networkclub-hub');
                      setShowProfileMenu(false);
                      window.location.hash = '#networkclub-hub';
                    }}
                    className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      profileSubPage === 'networkclub-hub'
                        ? 'text-blue-900 bg-blue-50' 
                        : 'text-gray-600 hover:text-blue-900 hover:bg-blue-50'
                    }`}
                    title="Admin: Test Netwerkclub Hub"
                  >
                    🏢 Netwerkclub
                  </button>
                  
                  <button
                    onClick={() => {
                      setActivePage('dashboard');
                      setProfileSubPage('mastermind-hub');
                      setShowProfileMenu(false);
                      window.location.hash = '#mastermind-hub';
                    }}
                    className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      profileSubPage === 'mastermind-hub'
                        ? 'text-indigo-900 bg-indigo-50' 
                        : 'text-gray-600 hover:text-indigo-900 hover:bg-indigo-50'
                    }`}
                    title="Admin: Test Mastermind Hub"
                  >
                    🧠 Mastermind
                  </button>

                  {/* Seed test mastermind data */}
                  <button
                    onClick={async () => {
                      const result = await seedTestMastermind();
                      alert(result.message);
                    }}
                    className="relative px-4 py-2 text-sm font-medium rounded-lg transition-all text-gray-600 hover:text-emerald-900 hover:bg-emerald-50"
                    title="Maak test-mastermind aan voor test@superkonnected.nl en test1@superkonnected.nl"
                  >
                    🌱 Seed Mastermind
                  </button>
                  
                  <button
                    onClick={() => {
                      setActivePage('admin-monitoring');
                      setProfileSubPage(null);
                      setShowProfileMenu(false);
                    }}
                    className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      activePage === 'admin-monitoring' 
                        ? 'text-purple-900 bg-purple-50' 
                        : 'text-gray-600 hover:text-purple-900 hover:bg-purple-50'
                    }`}
                  >
                    Admin
                  </button>
                </>
              )}
              
              {/* AI Test & Social Sharing tijdelijk uit nav verbergd */}
              {/*
              <button
                onClick={() => {
                  setActivePage('gemini-test');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                className={`relative group px-1.5 py-2 text-sm transition-colors ${
                  activePage === 'gemini-test' ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Test Gemini AI"
              >
                🤖 AI Test
                <span
                  aria-hidden
                  className={`pointer-events-none absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-[#10B981] via-[#34D399] to-[#6EE7B7] transition-opacity ${
                    activePage === 'gemini-test' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                />
              </button>
              <button
                onClick={() => {
                  setActivePage('social-sharing');
                  setProfileSubPage(null);
                  setShowProfileMenu(false);
                }}
                className={`relative group px-1.5 py-2 text-sm transition-colors ${
                  activePage === 'social-sharing' ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Test Social Sharing APIs"
              >
                📤 Social Sharing
                <span
                  aria-hidden
                  className={`pointer-events-none absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-[#7A9488] via-[#9AAF88] to-[#8A9E78] transition-opacity ${
                    activePage === 'social-sharing' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                />
              </button>
              */}
            </nav>

            <div className="flex items-center gap-3 relative">
              <div className="relative" data-profile-menu>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="w-10 h-10 md:w-8 md:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-all touch-manipulation"
                  aria-label="Menu"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                
                {showProfileMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  {/* Mobile-only: Display & Filters shortcut */}
                  <button
                    onClick={() => {
                      setActivePage('dashboard');
                      setProfileSubPage('profile-hub');
                      setShowProfileMenu(false);
                      window.location.hash = '#profile-hub';
                    }}
                    className="md:hidden w-full text-left px-4 py-3 text-sm font-medium text-[#4F6660] bg-[rgba(79,102,96,0.06)] hover:bg-[rgba(79,102,96,0.12)] transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Weergave &amp; Filters
                  </button>
                  <div className="md:hidden border-t border-gray-100 my-1"></div>
                  {canShowPage('mijn-verhaal') && (
                  <button
                    onClick={() => { 
                      setActivePage('mijn-verhaal'); 
                      setProfileSubPage(null); 
                      setShowProfileMenu(false); 
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
                  >
                    Mijn Verhaal
                  </button>
                  )}
                  <button
                    onClick={() => { 
                      setActivePage('dashboard'); 
                      setProfileSubPage('profile-hub'); 
                      setShowProfileMenu(false); 
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
                  >
                    Mijn Profiel
                  </button>
                  {user && (
                  <button
                    onClick={() => { 
                      setActivePage('dashboard'); 
                      setProfileSubPage('llm-dashboard'); 
                      setShowProfileMenu(false); 
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
                  >
                    <InsightIcon size={16} weight="regular" className="text-gray-500" />
                    <span>LLM Expertstatus</span>
                  </button>
                  )}
                  {canShowPage('membership') && (
                  <button
                    onClick={() => { 
                      setActivePage('dashboard'); 
                      setProfileSubPage('membership'); 
                      setShowProfileMenu(false); 
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
                  >
                    Lidmaatschap
                  </button>
                  )}
                  <button
                    onClick={() => {
                      setActivePage('abonnement');
                      setProfileSubPage(null);
                      setShowProfileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
                  >
                    <span className="text-amber-500">✦</span>
                    <span>Abonnementen</span>
                    {currentTier === 'lifetime' && <span className="ml-auto text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">Founding</span>}
                    {currentTier === 'free' && <span className="ml-auto text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">Gratis</span>}
                  </button>
                  <button
                    onClick={() => { 
                      setActivePage('dashboard'); 
                      setProfileSubPage('groepsbeheer'); 
                      setShowProfileMenu(false); 
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
                  >
                    <CommunityIcon size={16} weight="regular" className="text-gray-500 shrink-0" />
                    <span>Mijn Groepen</span>
                  </button>
                  
                  {/* Deel pagina optie */}
                  <div className="border-t border-gray-100 my-1"></div>
                  <PageInviteMenuItem 
                    pageName={getCurrentPageName(activePage)}
                    pageHash={getCurrentPageHash(activePage)}
                    userName={userProfile?.displayName}
                    onClose={() => setShowProfileMenu(false)}
                  />
                  
                  {/* Credits - minimalistisch zoals Deel pagina */}
                  {user && (
                  <button
                    onClick={() => {
                      setShowCreditsInfo(true);
                      setShowProfileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2"><CreditCard size={16} className="text-gray-500" /> Credits</span>
                    <span className="font-medium">{creditsBalance || 0}</span>
                  </button>
                  )}
                  
                  {isAdmin && (
                    <>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => {
                          const newMode = viewMode === 'admin' ? 'member' : 'admin';
                          setViewMode(newMode);
                          console.log('🔄 View mode changed to:', newMode);
                          // Force re-render by closing menu
                          setShowProfileMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          viewMode === 'admin' 
                            ? 'hover:bg-purple-50 text-purple-600' 
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {viewMode === 'admin' ? '→ Lid View' : '→ Admin View'}
                      </button>
                    </>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Direct sign out - menu sluit automatisch door state change
                      handleSignOut();
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 transition-colors text-red-600"
                  >
                    Uitloggen
                  </button>
                  </div>
                )}
              </div>
            </div>
          </AutoFadeNavbar>
        </div>
      </div>

             {/* Page Content
                  Mobile dashboard: fixed height = 100dvh - 42px (PageTabs in flow) - 58px (BottomNav fixed) = calc(100dvh-100px).
                  pt-[52px] stays inside so content starts below MobileTopBar.
                  overflow-y-hidden prevents outer page scroll; inner feed scrolls itself. */}
       <div className={`mobile-content-wrapper max-w-[100vw] overflow-x-hidden ${activePage === 'dashboard' && !profileSubPage && mobileSubTabIdx !== 0 ? 'bg-white' : 'bg-[#edeae4]'} md:bg-gray-50 md:pb-0 pt-[52px] md:pt-0 h-[calc(100dvh-100px)] md:h-auto md:min-h-screen md:overflow-visible ${
         (activePage === 'dashboard' && !profileSubPage) || activePage === 'connecties'
           ? 'overflow-y-hidden'
           : 'overflow-y-auto'
       }`}>
         {/* Main Pages */}
        <AdminMuteProvider isAdmin={isAdmin && viewMode === 'admin'}>
          <div className="h-full md:h-auto">
           {/* Offline page placeholder — shown when admin has toggled a page offline */}
           {!canShowPage(profileSubPage || activePage) && (
             <div className="flex items-center justify-center min-h-[60vh] p-8">
               <div className="text-center max-w-md">
                 <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                   <span className="text-4xl">🚧</span>
                 </div>
                 <h2 className="text-2xl font-bold text-gray-800 mb-3">Pagina niet beschikbaar</h2>
                 <p className="text-gray-500 mb-6">
                   Deze pagina is tijdelijk offline gezet door de beheerder. Probeer het later opnieuw.
                 </p>
                 <button
                   onClick={() => { window.location.hash = '#dashboard'; }}
                   className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all shadow-md"
                 >
                   Terug naar Dashboard
                 </button>
               </div>
             </div>
           )}
           {activePage === 'dashboard' && !profileSubPage && (
            <React.Suspense fallback={<div className="p-6">Loading…</div>}>
              <CommunityDashboard 
                user={user} 
                userProfile={userProfile} 
                isAdmin={isAdmin && viewMode === 'admin'} 
                onNavigateToUserStory={navigateToUserStory}
                subscription={subscription}
                canAccessFeature={canAccessFeature}
                onStartTrial={startTrial}
                onUpgrade={upgradeToPremium}
                displaySettings={displaySettings}
                setDisplaySettings={setDisplaySettings}
                mobileTabIndex={mobileSubTabIdx}
                // setActivePage removed
              />
            </React.Suspense>
           )}
           {activePage === 'connecties' && canShowPage('connecties') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <ConnectiesPage userProfile={userProfile} mobileTabIndex={mobileSubTabIdx} />
             </React.Suspense>
           )}
           {activePage === 'chat-system' && canShowPage('chat-system') && <ChatConnectionSystem />}
           {activePage === 'chat' && canShowPage('chat') && <ChatInterface />}
           {activePage === 'agenda' && canShowPage('agenda') && <AgendaPage userProfile={userProfile} mobileTabIndex={mobileSubTabIdx} />}
           {activePage === 'inbox' && canShowPage('inbox') && <InboxPage userProfile={userProfile} mobileTabIndex={mobileSubTabIdx} />}
           {activePage === 'expert-profile' && expertSlug && (
             <PublicExpertProfile slug={expertSlug} />
           )}
           {activePage === 'vibes' && canShowPage('vibes') && (
             profileSubPage === 'english' ? (
               <div className="p-6">
                 <div className="max-w-4xl mx-auto">
                   <div className="text-center mb-8">
                     <h1 className="text-4xl font-bold text-gray-900 mb-4">
                       VibeScore - Analyze the nourishment of your content
                     </h1>
                     <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                       Analyze your post with VibeScore, see how your words resonate, and improve your Clean Mind impact.
                     </p>
                   </div>
                   
                   <div className="bg-gradient-to-br from-purple-50 to-blue-100 p-8 rounded-xl border border-purple-200 mb-8">
                     <h2 className="text-2xl font-semibold text-gray-900 mb-4">How VibeScore Works</h2>
                     <div className="grid md:grid-cols-3 gap-6">
                       <div className="text-center">
                         <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                           <span className="text-2xl text-white">📝</span>
                         </div>
                         <h3 className="font-semibold text-gray-900 mb-2">1. Write Your Content</h3>
                         <p className="text-sm text-gray-600">Create your post or message as you normally would</p>
                       </div>
                       <div className="text-center">
                         <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                           <span className="text-2xl text-white">🔍</span>
                         </div>
                         <h3 className="font-semibold text-gray-900 mb-2">2. Real-time Analysis</h3>
                         <p className="text-sm text-gray-600">VibeScore analyzes sentiment, toxicity and positive signals</p>
                       </div>
                       <div className="text-center">
                         <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                           <BarChart3 size={32} strokeWidth={2} className="text-white" />
                         </div>
                         <h3 className="font-semibold text-gray-900 mb-2">3. Get Your Score</h3>
                         <p className="text-sm text-gray-600">See your score and color meter for instant feedback</p>
                       </div>
                     </div>
                   </div>
                   
                   <div className="text-center">
                     <button
                       onClick={() => {
                         setActivePage('dashboard');
                         setProfileSubPage(null);
                         setShowProfileMenu(false);
                         window.location.hash = '#dashboard';
                       }}
                       className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                     >
                       Try VibeScore Now
                     </button>
                   </div>
                 </div>
               </div>
             ) : (
              <VibesPage user={user} userProfile={userProfile} mobileTabIndex={mobileSubTabIdx} />
             )
           )}
          {activePage === 'admin-schedule' && isAdmin && viewMode === 'admin' && <AdminSchedulePage />}
           {activePage === 'admin-monitoring' && isAdmin && viewMode === 'admin' && <AdminMonitoringDashboard />}
           {activePage === 'privacy' && <PrivacyPolicy />}
           {activePage === 'terms' && <TermsOfService />}
           {activePage === 'abonnement' && (
             <React.Suspense fallback={<div className="p-6 text-center text-gray-500">Laden…</div>}>
               <SubscriptionPage
                 currentSubscription={subscription}
                 onStartTrial={() => void startTrial()}
                 onUpgrade={() => void upgradeToPremium()}
               />
             </React.Suspense>
           )}
           {/* Test pages - hidden in funding mode */}
           {SHOW_GEMINI_TEST && activePage === 'gemini-test' && (
             <div className="p-6">
               <GeminiTestComponent />
             </div>
           )}
           {activePage === 'planning-test' && (
             <PlanningNotifTest />
           )}
           {activePage === 'fysieke-test' && (
             <FysiekePhaseTest />
           )}
           {SHOW_SOCIAL_SHARING_DEMO && activePage === 'social-sharing' && (
             <SocialSharingDemo />
           )}
           {activePage === 'social-media' && canShowPage('social-media') && user && (
            <SocialMediaHub />
          )}
          {activePage === 'mijn-verhaal' && canShowPage('mijn-verhaal') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <ProfileSliderHomeFromVerhaal
                 selectedUserId={selectedUserForStory}
                 onBack={storyReturnPage ? handleStoryBack : undefined}
               />
             </React.Suspense>
           )}
          {activePage === 'club-verhaal' && selectedClubForStory && (
            <ClubVerhaalPage
              entityId={selectedClubForStory.entityId}
              entityType={selectedClubForStory.entityType}
              onBack={storyReturnPage ? handleStoryBack : undefined}
            />
          )}


           {/* Profile Submenu Pages */}
           {profileSubPage === 'profile-hub' && canShowPage('profile-hub') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <ProfileHub
                 userProfile={userProfile}
                 displaySettings={displaySettings}
                 onDisplaySettingsChange={setDisplaySettings}
               />
             </React.Suspense>
           )}
           {profileSubPage === 'networkclub-hub' && canShowPage('networkclub-hub') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <NetworkClubProfileHub />
             </React.Suspense>
           )}
           {profileSubPage === 'mastermind-hub' && canShowPage('mastermind-hub') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <MastermindCommunityHub />
             </React.Suspense>
           )}
           {profileSubPage === 'cards-editor' && canShowPage('cards-editor') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <DragDropCardsEditor />
             </React.Suspense>
           )}
           {profileSubPage === 'llm-dashboard' && user && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <LLMAnalysesDropdown
                 userId={user.uid}
                 onNavigate={(page, subPage) => {
                   setActivePage(page as any);
                   if (subPage) {
                     setProfileSubPage(subPage as any);
                   }
                 }}
                 asPage={true}
               />
             </React.Suspense>
           )}
           {profileSubPage === 'membership' && canShowPage('membership') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <MembershipPage />
             </React.Suspense>
           )}
           {profileSubPage === 'groepsbeheer' && (
             <GroupsManager onClose={() => setProfileSubPage(null)} />
           )}
           {profileSubPage === 'training' && canShowPage('training') && (
             <React.Suspense fallback={<div className="p-6">Loading…</div>}>
               <TrainingCenter isAdmin={isAdmin && viewMode === 'admin'} />
             </React.Suspense>
           )}
          {profileSubPage === 'english' && canShowPage('en') && (
             <div className="p-6">
               <div className="max-w-4xl mx-auto">
                 <div className="text-center mb-8">
                   <h1 className="text-4xl font-bold text-gray-900 mb-4">
                     SuperKonnected - The first Clean Mind Social Media platform of the world, for entrepreneurs
                   </h1>
                   <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                     Build real connections with entrepreneurs, reduce noise, and feed your mind with positive, practical content.
                     VibeScore gives instant feedback with a clear score and a subtle color meter.
                   </p>
                 </div>
                 
                 <div className="grid md:grid-cols-2 gap-8 mb-12">
                   <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200">
                     <h2 className="text-2xl font-semibold text-gray-900 mb-4">🌈 VibeScore Technology</h2>
                     <p className="text-gray-700 mb-4">
                       Our proprietary VibeScore analyzes posts for sentiment, toxicity and nourishing signals, 
                       showing a clear score and subtle color meter to help you create more positive content.
                     </p>
                     <div className="flex items-center space-x-2 text-sm text-blue-600">
                       <BarChart3 size={16} strokeWidth={1.5} />
                       <span>Real-time analysis</span>
                     </div>
                   </div>
                   
                   <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-xl border border-green-200">
                     <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                       <HandshakeIcon size={24} strokeWidth={1.5} className="text-green-600" />
                       Quality Networking
                     </h2>
                     <p className="text-gray-700 mb-4">
                       Connect with like-minded entrepreneurs who share your values. 
                       Our platform focuses on meaningful relationships over vanity metrics.
                     </p>
                     <div className="flex items-center space-x-2 text-sm text-green-600">
                       <Target size={16} strokeWidth={1.5} />
                       <span>Targeted connections</span>
                     </div>
                   </div>
                 </div>
                 
                 <div className="text-center">
                   <div className="flex flex-col sm:flex-row gap-4 justify-center">
                     <button
                       onClick={() => {
                         setActivePage('dashboard');
                         setProfileSubPage(null);
                         setShowProfileMenu(false);
                         window.location.hash = '#dashboard';
                       }}
                       className="px-8 py-3 bg-gradient-to-r from-[#7A9488] to-red-700 text-white font-semibold rounded-lg hover:from-[#4F6660] hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                     >
                       Get Started
                     </button>
                     <button
                       onClick={() => {
                         setActivePage('vibes');
                         setProfileSubPage('english');
                         setShowProfileMenu(false);
                         window.location.hash = '#en/vibescore';
                       }}
                       className="px-8 py-3 bg-white border-2 border-[#7A9488] text-[#7A9488] font-semibold rounded-lg hover:bg-stone-50 transition-all duration-200"
                     >
                       Explore VibeScore
                     </button>
                   </div>
                 </div>
               </div>
             </div>
          )}

          {/* Global toast voor inkomende connectieverzoeken */}
          {connectionRequestToast && (
            <div className="fixed bottom-6 right-6 z-[9999] animate-slide-in">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 max-w-sm flex items-start gap-3">
                {connectionRequestToast.fromPhoto ? (
                  <img src={connectionRequestToast.fromPhoto} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">{connectionRequestToast.fromName.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{connectionRequestToast.fromName}</p>
                  <p className="text-xs text-gray-500 mt-0.5">heeft je een connectieverzoek gestuurd</p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => {
                        window.location.hash = '#connecties';
                        setConnectionRequestToast(null);
                      }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Bekijken
                    </button>
                    <button
                      onClick={() => setConnectionRequestToast(null)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Later
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setConnectionRequestToast(null)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          )}

          {/* Pending planning reminder toast (shown after login) */}
          {pendingPlanningToast && (
            <div className="fixed bottom-6 left-6 z-[9999] animate-slide-in">
              <div className="bg-white rounded-2xl shadow-2xl border border-amber-200 p-4 max-w-sm flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-600 text-lg">📅</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Openstaande planning</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Je was bezig met plannen met <strong>{pendingPlanningToast.otherUserName}</strong> — ga verder waar je gebleven was.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={async () => {
                        // Mark inbox item as read
                        try {
                          const { doc: fsDoc, updateDoc: fsUpdateDoc } = await import('firebase/firestore');
                          await fsUpdateDoc(fsDoc(db, 'inboxItems', pendingPlanningToast.inboxItemId), { isRead: true });
                        } catch (_) {}
                        // Navigate to connecties and open this connection
                        setActivePage('connecties');
                        window.location.hash = `#connecties?open=${pendingPlanningToast.connectionId}`;
                        setPendingPlanningToast(null);
                      }}
                      className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg hover:bg-amber-600 transition-colors"
                    >
                      Ga nu plannen
                    </button>
                    <button
                      onClick={() => setPendingPlanningToast(null)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Later
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setPendingPlanningToast(null)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Taken Reminder toast ── */}
          {taskReminderToast && (
            <div className="fixed bottom-6 left-6 z-[9999] animate-slide-in">
              <div className="bg-white rounded-2xl shadow-2xl border border-emerald-200 p-4 max-w-sm flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{taskReminderToast.groupName}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Je hebt nog <strong>{taskReminderToast.openCount} open {taskReminderToast.openCount === 1 ? 'taak' : 'taken'}</strong> staan als commitment.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => {
                        setActivePage('agenda');
                        setTaskReminderToast(null);
                      }}
                      className="px-3 py-1.5 bg-emerald-500 text-white text-xs font-semibold rounded-lg hover:bg-emerald-600 transition-colors"
                    >
                      Bekijk taken
                    </button>
                    <button
                      onClick={() => setTaskReminderToast(null)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Later
                    </button>
                  </div>
                </div>
                <button onClick={() => setTaskReminderToast(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          )}

          {/* Global popup voor nieuw geaccepteerde connecties */}
          {newConnectionPopup && user && (
            <ConnectionAcceptedModal
              connectionId={newConnectionPopup.id}
              otherUserId={newConnectionPopup.user1Id === user.uid ? newConnectionPopup.user2Id : newConnectionPopup.user1Id}
              otherUsername={
                newConnectionPopup.user1Id === user.uid 
                  ? (newConnectionPopup.user2Name || 'Connectie')
                  : (newConnectionPopup.user1Name || 'Connectie')
              }
              otherUserPhoto={
                newConnectionPopup.user1Id === user.uid 
                  ? newConnectionPopup.user2Photo 
                  : newConnectionPopup.user1Photo
              }
              currentUserId={user.uid}
              currentUsername={user.displayName || 'Jij'}
              onClose={() => {
                        markConnectionNotificationAsSeen(newConnectionPopup.id);
                setNewConnectionPopup(null);
              }}
              onStartChat={() => {
                console.log('🚀 App.tsx onStartChat triggered for:', newConnectionPopup.id);
                markConnectionNotificationAsSeen(newConnectionPopup.id);
                console.log('📍 Setting hash to #connecties');
                        window.location.hash = '#connecties';
                console.log('📢 Dispatching openConnectionFlowForConnection event with mode: chat');
                        window.dispatchEvent(
                          new CustomEvent('openConnectionFlowForConnection', {
                            detail: { connectionId: newConnectionPopup.id, mode: 'chat' }
                          })
                        );
                console.log('🔒 Closing popup');
                        setNewConnectionPopup(null);
                      }}
              onStartPlanning={() => {
                        markConnectionNotificationAsSeen(newConnectionPopup.id);
                        window.location.hash = '#connecties';
                        window.dispatchEvent(
                          new CustomEvent('openConnectionFlowForConnection', {
                            detail: { connectionId: newConnectionPopup.id, mode: 'agenda' }
                          })
                        );
                        setNewConnectionPopup(null);
                      }}
            />
          )}

          {/* Global live meeting toasts — non-blocking, stacked top-right */}
          {liveToasts.length > 0 && (
            <div className="fixed top-4 right-4 z-[10000] flex flex-col gap-2 pointer-events-none" style={{ maxWidth: 380 }}>
              {liveToasts.map((toast) => {
                const typeLabel = toast.entityType === 'mastermind' ? 'Mastermind'
                  : toast.entityType === 'networkclub' ? 'Netwerkclub'
                  : 'Community';
                const hubType = toast.entityType === 'networkclub' ? 'networkclub-hub' : 'mastermind-hub';
                return (
                  <div
                    key={toast.entityId}
                    className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-emerald-200 overflow-hidden animate-slideInRight"
                  >
                    {/* Pulsing live bar */}
                    <div className="h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-400 animate-pulse" />
                    <div className="p-3.5 flex items-start gap-3">
                      {/* Live indicator dot */}
                      <div className="relative mt-1 shrink-0">
                        <span className="block w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-400 animate-ping" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-600">{typeLabel}</span>
                          <span className="text-[9px] uppercase tracking-wider font-bold text-red-500 animate-pulse">LIVE</span>
                        </div>
                        <p className="text-sm font-bold text-gray-900 truncate mt-0.5">{toast.entityName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          <strong className="text-gray-700">{toast.startedByName}</strong> is live gegaan
                        </p>
                      </div>
                      {/* Close */}
                      <button
                        onClick={() => {
                          const key = `${toast.entityId}:${toast.roomName}`;
                          dismissedMastermindLiveKeysRef.current.add(key);
                          setLiveToasts((prev) => prev.filter((t) => t.entityId !== toast.entityId));
                          if (mastermindLiveInvite?.entityId === toast.entityId) setMastermindLiveInvite(null);
                        }}
                        className="text-gray-300 hover:text-gray-500 transition-colors shrink-0"
                        aria-label="Sluiten"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    {/* Join button */}
                    <div className="px-3.5 pb-3">
                      <button
                        onClick={() => {
                          const key = `${toast.entityId}:${toast.roomName}`;
                          dismissedMastermindLiveKeysRef.current.add(key);
                          try { sessionStorage.setItem('pendingMastermindJoinEntityId', toast.entityId); } catch {}
                          localStorage.setItem('activeEntityId', toast.entityId);
                          window.dispatchEvent(new CustomEvent('activeEntityChanged', { detail: { entityId: toast.entityId } }));
                          window.dispatchEvent(new CustomEvent('mastermindAutoJoin', { detail: { entityId: toast.entityId, roomName: toast.roomName } }));
                          setActivePage('dashboard');
                          setProfileSubPage(hubType as any);
                          setShowProfileMenu(false);
                          window.location.hash = `#${hubType}`;
                          setLiveToasts((prev) => prev.filter((t) => t.entityId !== toast.entityId));
                          setMastermindLiveInvite(null);
                        }}
                        className="w-full h-8 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5"
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        Deelnemen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>


      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(100%); }
            to   { opacity: 1; transform: translateX(0); }
          }
          .animate-slideInRight {
            animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
          }
          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateY(20px);
            }
            to { 
              opacity: 1; 
              transform: translateY(0);
            }
          }
          @keyframes fadeInDiagonal {
            from { 
              opacity: 0; 
              transform: translate(-30px, 30px) rotate(-2deg);
            }
            to { 
              opacity: 1; 
              transform: translate(0, 0) rotate(0deg);
            }
          }
          @keyframes fadeInTextDiagonal {
            0% { 
              opacity: 0; 
              transform: translate(-40px, -20px);
            }
            100% { 
              opacity: 1; 
              transform: translate(20px, 10px);
            }
          }
          @keyframes fadeInQuote {
            0% { 
              opacity: 0; 
              transform: translateY(30px);
            }
            100% { 
              opacity: 1; 
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.8s ease-out forwards;
          }
          .animate-fadeIn-delayed {
            opacity: 0;
            animation: fadeIn 0.8s ease-out 0.5s forwards;
          }
          .animate-fadeIn-diagonal {
            opacity: 0;
            animation: fadeInDiagonal 0.8s ease-out forwards;
          }
          .animate-fadeIn-text-diagonal {
            opacity: 0;
            animation: fadeInTextDiagonal 3s ease-out forwards;
            animation-fill-mode: both;
          }
          .animate-fadeIn-quote {
            opacity: 0;
            animation: fadeInQuote 5s ease-out forwards;
            animation-fill-mode: both;
          }
        `
      }} />

      {showConnectionFlow && (
        <ConnectionFlowContainer
          connectionId="test-connection-123"
          currentUserId={user?.uid || 'user123'}
          onClose={() => setShowConnectionFlow(false)}
        />
      )}

      {/* Global Display Settings Modal */}
      {showDisplaySettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDisplaySettings(false)} />
          <div className="relative bg-white rounded-2xl shadow-lg border w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Display Instellingen</h2>
              <button onClick={() => setShowDisplaySettings(false)} aria-label="Sluiten" className="w-8 h-8 rounded-full hover:bg-gray-100">✕</button>
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-center justify-between">
                <span>Compacte kaarten</span>
                <input type="checkbox" onChange={(e) => {
                  try { localStorage.setItem('display.compactCards', e.target.checked ? '1' : '0'); window.dispatchEvent(new CustomEvent('displaySettingsChanged')) } catch {}
                }} defaultChecked={typeof window !== 'undefined' && localStorage.getItem('display.compactCards') === '1'} />
              </div>
              <div className="flex items-center justify-between">
                <span>Grotere lettertypes</span>
                <input type="checkbox" onChange={(e) => {
                  try { localStorage.setItem('display.largeFonts', e.target.checked ? '1' : '0'); window.dispatchEvent(new CustomEvent('displaySettingsChanged')) } catch {}
                }} defaultChecked={typeof window !== 'undefined' && localStorage.getItem('display.largeFonts') === '1'} />
              </div>
              <div className="flex items-center justify-between">
                <span>Donkere modus</span>
                <input type="checkbox" onChange={(e) => {
                  try { localStorage.setItem('display.darkMode', e.target.checked ? '1' : '0'); document.documentElement.classList.toggle('dark', e.target.checked); window.dispatchEvent(new CustomEvent('displaySettingsChanged')) } catch {}
                }} defaultChecked={typeof window !== 'undefined' && localStorage.getItem('display.darkMode') === '1'} />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowDisplaySettings(false)} className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">Gereed</button>
            </div>
          </div>
        </div>
      )}
        </AdminMuteProvider>
      </div>
    </div>

    {/* Globale agenda-melding – beschikbaar op alle pagina's */}
    <GlobalAgendaNotification userProfile={userProfile} />
    {/* Floating meeting wijzigingsverzoek notificatie – zichtbaar op elke pagina */}
    <FloatingMeetingNotification userProfile={userProfile} />
    
    {/* Manager Access Request Handler - Shows popup for pending manager requests */}
    {user && userProfile && (
      <ManagerAccessRequestHandler
        userId={user.uid}
        isEntrepreneur={userProfile.onboardingData?.accountType === 'entrepreneur' || !userProfile.isManager}
      />
    )}
    
    {/* Account Transfer Request Popup - Shows when someone wants to transfer their account to you */}
    {user && <AccountTransferRequestPopup userId={user.uid} />}
    
    {/* AI Assistant Overlay - Altijd gemount voor ingelogde gebruikers */}
    {user && (
      <AIAssistantOverlay 
        userName={userProfile?.firstName || userProfile?.displayName?.split(' ')[0] || user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'Gebruiker'}
        onNavigate={(target) => {
          console.log('🚀 AI Navigation to:', target);
          setActivePage(target as any);
          window.location.hash = target;
        }}
        onClose={() => {
          const hash = (typeof window !== 'undefined' ? window.location.hash.slice(1) : '').split('/')[0];
          if (hash === 'terms' || hash === 'privacy') {
            setActivePage('dashboard');
            window.location.hash = '#dashboard';
          }
        }}
      />
    )}
    {/* Experience Mode Overlay */}
    <ExperienceModeOverlay viewMode={viewMode} />
    
    {/* Experience Mode - Page Completion Card */}
    <PageCompletionCard />
    
    {/* Experience Mode - Green Tour Button - AFTER overlay to ensure it's above */}
    {user && activePage === 'dashboard' && !profileSubPage && (
      <GreenTourButton currentPageId={activePage} viewMode={viewMode} />
    )}

    {/* Admin Page Navigator - zwevende knop voor admin om alle pagina's te bereiken */}
    {user && isAdmin && viewMode === 'admin' && <AdminPageNavigator isAdmin={isAdmin} viewMode={viewMode} />}

    {/* Feedback Widget - Desktop only (hidden on mobile per design spec) */}
    <div className="hidden md:block">
      <FeedbackWidget 
        userEmail={user?.email || undefined}
        userName={userProfile?.firstName || userProfile?.displayName || user?.displayName || undefined}
      />
    </div>
    {/* Platform Explanation Mode - Zichtbaar op alle pagina's */}
    <PlatformExplanationMode 
      isOpen={showExplanationMode}
      onClose={() => setShowExplanationMode(false)}
    />

    {/* Mobile Bottom Navigation — only visible below md (768px) */}
    {user && <BottomNavigation dimmed={mobileNavDimmed} />}

    {/* Credits Info Modal */}
    {showCreditsInfo && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
          <button
            onClick={() => setShowCreditsInfo(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>

          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-gradient-to-r from-stone-50 to-blue-50 rounded-lg">
              <span className="text-2xl">💰</span>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Credits Systeem</h3>
          </div>

          <div className="space-y-4 text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">💡 Wat zijn credits?</h4>
              <p className="text-sm">
                Credits zijn je persoonlijke tokens om AI-analyses te gebruiken op SuperKonnected.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-1">🤖 Waarvoor gebruik je ze?</h4>
              <ul className="text-sm space-y-1 list-disc list-inside">
                <li><strong>AI Gespreksanalyse</strong> (5 credits): Na een ConnectieFlow gesprek krijg je een samenvatting, groeipunten en actiepunten</li>
                <li>Toekomstige AI features komen er aan!</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-1">✨ Hoe verdien je credits?</h4>
              <ul className="text-sm space-y-1 list-disc list-inside">
                <li><strong>Referrals</strong>: Nodig ondernemers uit met jouw persoonlijke code</li>
                <li><strong>Milestones</strong>: Bereik Expert Status levels</li>
                <li><strong>Activiteit</strong>: Actief blijven in de ConnectieFlow</li>
              </ul>
            </div>

            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
              <p className="text-sm text-[#4F6660]">
                <strong>💳 Tip:</strong> Je kunt ook credits bijkopen (binnenkort beschikbaar).
                Check je referral dashboard in Instellingen voor meer manieren om credits te verdienen!
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowCreditsInfo(false)}
            className="mt-6 w-full px-4 py-2 bg-gradient-to-r from-[#7A9488] to-blue-600 text-white rounded-lg hover:from-[#4F6660] hover:to-blue-700 transition-colors font-semibold"
          >
            Begrepen!
          </button>
        </div>
      </div>
    )}

          {/* Event invitation lightbox — shown when member is online and has pending invitations */}
          {pendingEventInvite && user && (
            <EventLightbox
              inboxItemId={pendingEventInvite.id}
              eventId={pendingEventInvite.eventId || ''}
              clubId={pendingEventInvite.clubId}
              entityId={pendingEventInvite.entityId}
              clubName={pendingEventInvite.clubName}
              eventData={pendingEventInvite.eventData || {
                title: pendingEventInvite.title || 'Evenement',
                date: '',
                startTime: '',
              }}
              userId={user.uid}
              onDismiss={() => {
                // Remove from queue, show next
                const next = pendingEventQueue.slice(1);
                setPendingEventQueue(next);
                setPendingEventInvite(next[0] || null);
              }}
              onResponded={() => {
                const next = pendingEventQueue.slice(1);
                setPendingEventQueue(next);
                setPendingEventInvite(next[0] || null);
              }}
            />
          )}

          {/* Group invite landing modal — shown when user follows an invite link */}
          {showGroupJoinModal && pendingGroupInvite && user && (
            <GroupJoinLandingModal
              entityId={pendingGroupInvite.entityId}
              entityType={pendingGroupInvite.entityType}
              clubName={pendingGroupInvite.clubName}
              invitedBy={pendingGroupInvite.invitedBy}
              onClose={() => {
                setShowGroupJoinModal(false);
                setPendingGroupInvite(null);
                try { sessionStorage.removeItem('pendingGroupInvite'); } catch {}
              }}
              onJoined={(joinedEntityType) => {
                setShowGroupJoinModal(false);
                setPendingGroupInvite(null);
                try { sessionStorage.removeItem('pendingGroupInvite'); } catch {}
                const hubType = joinedEntityType === 'networkclub' ? 'networkclub-hub' : 'mastermind-hub';
                setActivePage('dashboard');
                setProfileSubPage(hubType as any);
                try { window.location.hash = `#${hubType}`; } catch {}
              }}
            />
          )}

        </ErrorBoundary>
      </ExperienceModeProvider>
    </HelmetProvider>
  );
};

export default App;
